import React, { useState, useEffect, useRef } from 'react';
import { formatDate, formatDateForInput, getCurrentDateForInput, getFutureDateForInput } from '../utils/dateUtils';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Stack,
  CardMedia,
  Skeleton,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  FilterList as FilterListIcon,
  Business as BusinessIcon,
  ShoppingCart as ShoppingCartIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { normalizeImageDataUrl, parseImageDataUrl } from '../utils/imageDataUtils';
import OfferForm, { checkOfferTopSpotRequirements } from '../components/OfferForm';
import AdvertisingOrderForm, { checkOrderTopSpotRequirements } from '../components/AdvertisingOrderForm';
import OrderAttachments from '../components/OrderAttachments';
import { uploadTempAttachments } from '../utils/attachmentUpload';
import NotesDialog from '../components/NotesDialog';
import OrderWorkflowNotesDialog from '../components/OrderWorkflowNotesDialog';
import OrganizationDialog from '../components/organizations/OrganizationDialog';

import OrganisationsMap from '../components/organizations/OrganisationsMap';
import EnquiryActions from '../components/EnquiryActions';
import DurationSelector from '../components/DurationSelector';
import EnhancedSearch from '../components/common/EnhancedSearch';
import ComprehensiveUpdateDialog from '../components/ComprehensiveUpdateDialog';
import WorkflowHistory from '../components/orders/WorkflowHistory';
import PricingService from '../services/pricingService';
import { collectAllNotesFromEnquiry, collectCategorizedNotesFromEnquiry, createOfferCreationNote } from '../utils/noteUtils';

/**
 * TabPanel Component - Defined outside to prevent re-rendering issues
 */
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`sales-tabpanel-${index}`}
    aria-labelledby={`sales-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

/**
 * Sales Workflow Page
 * Manages the complete sales process with enquiries, order tracking, and follow-ups
 */
const SalesWorkflow = () => {
  const { getToken, user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Outcome options - must match the main Enquiries page
  const outcomes = [
    'New',
    'Follow Up Later',
    'Ordered',
    'Not Interested',
    'Cancelled After Order'
  ];

  // Not interested reasons
  const notInterestedReasons = [
    'Only Want One-Off or Short Term Ad',
    'Too Expensive',
    "Don't Have Requested School/s",
    'School/s Full',
    'No Schools in Area',
    'Non-Responsive to Contact',
    'Chose to Advertise Elsewhere',
    'Seeking Quotes Only'
  ];
  const [tabValue, setTabValue] = useState(() => {
    const savedTab = localStorage.getItem('salesWorkflowActiveTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  
  // Real data states
  const [enquiries, setEnquiries] = useState([]);
  const [offers, setOffers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  // Outreach contacts marked "follow_up" — kept in a ref so they survive
  // re-calls of generateFollowUps triggered by the Enquiries / Orders tabs.
  const outreachFollowUpsCacheRef = useRef([]);

  // Outreach contact ID waiting to be linked to an offer being created from the outreach flow
  const [pendingOutreachContactId, setPendingOutreachContactId] = useState(null);
  
  // Reorder dialog state
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [reorderFormData, setReorderFormData] = useState({
    audiences: [], // Initialize with empty array to prevent undefined errors
    orderType: 'ad'
  });
  
  // Order filtering states - persist to localStorage
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [orderDateFilterType, setOrderDateFilterType] = useState(() => {
    const saved = localStorage.getItem('salesWorkflowOrderDateFilterType');
    return saved || 'month';
  });
  const [selectedOrderMonth, setSelectedOrderMonth] = useState(() => {
    const saved = localStorage.getItem('salesWorkflowSelectedOrderMonth');
    if (saved) return saved;
    // Default to current month (YYYY-MM format)
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedOrderYear, setSelectedOrderYear] = useState(() => {
    const saved = localStorage.getItem('salesWorkflowSelectedOrderYear');
    if (saved) return saved;
    // Default to current year
    const now = new Date();
    return now.getFullYear().toString();
  });
  const [orderStatusFilter, setOrderStatusFilter] = useState(() => {
    const saved = localStorage.getItem('salesWorkflowOrderStatusFilter');
    return saved || 'in_progress';
  });
  const [orderSortField, setOrderSortField] = useState('id');
  const [orderSortDir, setOrderSortDir]   = useState('desc');

  const handleOrderSort = (field) => {
    setOrderSortDir(prev => (orderSortField === field && prev === 'asc') ? 'desc' : 'asc');
    setOrderSortField(field);
  };
  
  // Follow-up filtering state
  const [followUpFilter, setFollowUpFilter] = useState('all'); // 'all', 'enquiry', 'order'
  
  // Change requests dialog state
  const [changeRequestsDialog, setChangeRequestsDialog] = useState({
    open: false,
    offerId: null,
    requests: []
  });

  // Image viewer dialog state
  const [imageViewerDialog, setImageViewerDialog] = useState({
    open: false,
    imageUrl: '',
    imageName: ''
  });

  // Notes dialog state
  const [notesDialog, setNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: '',
    defaultNoteType: 'general',
    notes: []
  });

  // Order tracking notes dialog state (using OrderWorkflowNotesDialog)
  const [orderNotesDialog, setOrderNotesDialog] = useState({
    open: false,
    title: '',
    entityType: '',
    entityId: '',
    notes: []
  });
  
  // Calculated stats from real data

  // Lazy loading and caching states
  const [dataCache, setDataCache] = useState({
    0: false, // Enquiries
    1: false, // Offers
    2: false, // Orders
    3: false  // Follow Ups
  });
  const [tabLoading, setTabLoading] = useState({
    0: false,
    1: false,
    2: false,
    3: false
  });

  // Real-time updates states
  const [eventSource, setEventSource] = useState(null);
  const [isUserBusy, setIsUserBusy] = useState(false);
  const [pendingRefreshes, setPendingRefreshes] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Organisation conversion states
  const [organizations, setOrganizations] = useState([]);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [enquiryToConvert, setEnquiryToConvert] = useState(null);
  const [token, setToken] = useState(null);
  const [organizationFormData, setOrganizationFormData] = useState({
    name: '',
    businessType: '',
    primaryName: '',
    primaryEmail: '',
    primaryPhone: '',
    generalEmail: '',
    generalPhone: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    notes: '',
    isBusiness: true,
    isSchool: false,
    isActive: true
  });

  // Order creation states


  // Map preview state — inline side panel inside the form dialog
  const [showMapPanel, setShowMapPanel] = useState(false);

  // Non-current follow-ups (lapsed orders < 2 years old)
  const [nonCurrentFollowUps, setNonCurrentFollowUps] = useState([]);
  const [nonCurrentLoading, setNonCurrentLoading] = useState(false);

  // Create offer states
  const [createOfferDialogOpen, setCreateOfferDialogOpen] = useState(false);
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [orderFormData, setOrderFormData] = useState({});
  const [orderTopSpotConflicts, setOrderTopSpotConflicts] = useState({});
  const [reorderTopSpotConflicts, setReorderTopSpotConflicts] = useState({});
  const [isEditingOffer, setIsEditingOffer] = useState(false);
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [selectedOfferForOrder, setSelectedOfferForOrder] = useState(null);
  const [offerFormData, setOfferFormData] = useState({
    businessType: 'existing', // 'existing' or 'new'
    organization_id: '',
    business_name: '',
    notes: '',
    offerNotes: '',
    designNotes: '',
    imageCaption: '',
    imageData: '',
    audiences: [{
      organization_id: '',
      channel: '',
      adType: '',
      adGroup: 1,
      startDate: '',
      endDate: '',
      price: '',
      discount: '0',
      discountType: '$'
    }]
  });
  const [offerTopSpotConflicts, setOfferTopSpotConflicts] = useState({});

  // Order edit dialog state
  const [editOrderDialogOpen, setEditOrderDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderFormData, setEditOrderFormData] = useState({});
  const [editOrderTopSpotConflicts, setEditOrderTopSpotConflicts] = useState({});

  // Create Update dialog state
  const [createUpdateDialogOpen, setCreateUpdateDialogOpen] = useState(false);
  const [selectedOrderForUpdate, setSelectedOrderForUpdate] = useState(null);
  const [selectedUpdateTypes, setSelectedUpdateTypes] = useState({
    dates: false,
    link: false,
    artwork: false
  });
  
  // Date update state
  const [selectedGroupForDateUpdate, setSelectedGroupForDateUpdate] = useState(null);
  const [newEndDate, setNewEndDate] = useState('');
  const [newDuration, setNewDuration] = useState('');
  const [dateUpdateCost, setDateUpdateCost] = useState(0);
  const [dateUpdateNoCost, setDateUpdateNoCost] = useState(false);
  
  // Link update state
  const [selectedGroupForLinkUpdate, setSelectedGroupForLinkUpdate] = useState(null);
  const [linkUpdateType, setLinkUpdateType] = useState('');
  const [linkUpdateContent, setLinkUpdateContent] = useState('');
  
  // Artwork update state
  const [artworkAttachments, setArtworkAttachments] = useState([]);
  const [artworkChangeCount, setArtworkChangeCount] = useState(0);
  const [artworkUpdateNoCost, setArtworkUpdateNoCost] = useState(false);
  
  const [updateNotes, setUpdateNotes] = useState('');

  // Calendar picker state
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarYear, setCalendarYear] = useState(() => {
    const [year] = selectedOrderMonth.split('-').map(Number);
    return year;
  });
  const [previousSelection, setPreviousSelection] = useState({
    month: selectedOrderMonth,
    year: selectedOrderYear,
    type: orderDateFilterType
  });

  // Helper functions for month/year navigation
  const navigateMonth = (direction) => {
    const [year, month] = selectedOrderMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    
    if (direction === 'prev') {
      date.setMonth(date.getMonth() - 1);
    } else {
      date.setMonth(date.getMonth() + 1);
    }
    
    const newYear = date.getFullYear();
    const newMonth = String(date.getMonth() + 1).padStart(2, '0');
    setSelectedOrderMonth(`${newYear}-${newMonth}`);
  };

  const navigateCalendarYear = (direction) => {
    if (direction === 'prev') {
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarYear(calendarYear + 1);
    }
  };

  const formatMonthDisplay = (monthString) => {
    const [year, month] = monthString.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleOpenCalendar = () => {
    // Save current selection
    setPreviousSelection({
      month: selectedOrderMonth,
      year: selectedOrderYear,
      type: orderDateFilterType
    });
    
    // Set calendar year to current selection
    const [year] = selectedOrderMonth.split('-').map(Number);
    setCalendarYear(year);
    setCalendarOpen(true);
  };

  const handleCloseCalendar = () => {
    setCalendarOpen(false);
  };

  const handleCancelCalendar = () => {
    // Revert to previous selection
    setSelectedOrderMonth(previousSelection.month);
    setSelectedOrderYear(previousSelection.year);
    setOrderDateFilterType(previousSelection.type);
    setCalendarOpen(false);
  };

  const handleSelectMonth = (monthIndex) => {
    const newMonth = String(monthIndex + 1).padStart(2, '0');
    setSelectedOrderMonth(`${calendarYear}-${newMonth}`);
    setOrderDateFilterType('month');
    setCalendarOpen(false);
  };

  const handleSelectYear = () => {
    setSelectedOrderYear(calendarYear.toString());
    setOrderDateFilterType('year');
    setCalendarOpen(false);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Helper function to check if addon duration requirements are met for UI validation
  const checkAddonDurationRequirements = (audiences = []) => {
    for (const audience of audiences) {
      if (audience.addons && audience.addons.topSpot) {
        if (!audience.startDate || !audience.endDate) {
          return false; // Missing dates
        }
        
        const startDate = new Date(audience.startDate);
        const endDate = new Date(audience.endDate);
        const diffTime = Math.abs(endDate - startDate);
        const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
        
        if (diffWeeks < 12) {
          return false; // Top Spot requires minimum 12 weeks
        }
      }
      
      // Add other addon validations here as needed
      // if (audience.addons.someOtherAddon && someCondition) { return false; }
    }
    return true; // All addon requirements met
  };

 // Debug log

  const getOutcomeColor = (outcome) => {
    const colors = {
      'New': 'primary',
      'Follow Up Later': 'warning',
      'Offered': 'info',
      'Not Interested': 'error',
      'Cancelled After Order': 'error'
    };
    return colors[outcome] || 'default';
  };

  const getOfferStatusColor = (status) => {
    const colors = {
      'New': 'primary',
      'Offered': 'info',
      'Generated': 'secondary',
      'Accepted': 'success',
      'Cancelled': 'error',
      'Needs Changes': 'warning'
    };
    return colors[status] || 'default';
  };

  // Helper function to get last 3 months including current month
  const getLastThreeMonths = () => {
    const months = [];
    const now = new Date();
    
    for (let i = 0; i < 3; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    
    return months;
  };

  // Helper function to get all months for the current year
  const getAllMonths = () => {
    const months = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    
    for (let month = 0; month < 12; month++) {
      const date = new Date(currentYear, month, 1);
      const value = `${currentYear}-${String(month + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    
    return months;
  };

  // Helper function to get available years (last 5 years including current)
  const getAvailableYears = () => {
    const years = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    
    for (let i = 0; i < 5; i++) {
      const year = currentYear - i;
      years.push({ value: year.toString(), label: year.toString() });
    }
    
    return years;
  };

  // Helper functions for attachment handling
  const isImageFile = (filename) => {
    if (!filename) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
    return imageExtensions.includes(extension);
  };

  const getAttachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return '';
    // Use the base URL without /api for static files
    const baseUrl = API_URL.replace('/api', '');
    const url = `${baseUrl}/uploads/${attachmentPath}`;
    return url;
  };

  const handleViewImage = (attachmentPath, originalName) => {
    setImageViewerDialog({
      open: true,
      imageUrl: getAttachmentUrl(attachmentPath),
      imageName: originalName
    });
  };

  const handleDownloadAttachment = async (attachmentPath, originalName) => {
    try {
      const url = getAttachmentUrl(attachmentPath);
      
      // Fetch the file as blob to force download
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }
      
      const blob = await response.blob();
      
      // Create object URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to opening in new tab if download fails
      const url = getAttachmentUrl(attachmentPath);
      window.open(url, '_blank');
    }
  };

  // Helper function to format audiences list for display
  const formatAudiencesList = (audiences) => {
    
    if (!audiences) return 'No audiences (null)';
    if (audiences.length === 0) return 'No audiences (empty array)';
    
    // Extract unique school names only
    const uniqueSchools = [...new Set(audiences.map(a => a.organizationName).filter(Boolean))];
    
    if (uniqueSchools.length === 0) {
      return 'No schools';
    }
    
    if (uniqueSchools.length === 1) {
      return uniqueSchools[0];
    }
    
    // For multiple schools, show them as a comma-separated list
    return uniqueSchools.join(', ');
  };

  useEffect(() => {
    loadTabData(tabValue);
  }, []);

  // Load data when tab changes
  useEffect(() => {
    loadTabData(tabValue);
  }, [tabValue]);

  // When navigated here from "New Order" on the Organisations page,
  // auto-open the create offer dialog with the org prefilled
  useEffect(() => {
    const { prefillOrg, openCreateOffer, outreach_contact_id } = location.state || {};
    if (!openCreateOffer || !prefillOrg) return;

    // If this offer is being created from an outreach contact, remember the ID so we
    // can link the offer back once it's saved.
    if (outreach_contact_id) {
      setPendingOutreachContactId(outreach_contact_id);
    }

    // Prefill the offer form with the known org data.
    // If the business has no DB id (typed into Outreach but not yet an Organisation
    // record), auto-select "New Business" so the name field is shown and pre-filled.
    // If it has an id, keep "Existing Business" so the org picker is shown.
    setOfferFormData(prev => ({
      ...prev,
      businessType:    prefillOrg.id ? 'existing' : 'new',
      organization_id: prefillOrg.id || '',
      business_name:   prefillOrg.name || '',
      offer_source:    'manual',
    }));
    setIsEditingOffer(false);
    setEditingOfferId(null);
    setCreateOfferDialogOpen(true);

    // Clear the navigation state so a refresh doesn't re-open the dialog
    navigate(location.pathname, { replace: true, state: {} });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle URL parameters for tab selection
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 3) {
        setTabValue(tabIndex);
      }
    }
  }, [searchParams]);

  // Persist order filter states to localStorage
  useEffect(() => {
    localStorage.setItem('salesWorkflowOrderStatusFilter', orderStatusFilter);
  }, [orderStatusFilter]);

  useEffect(() => {
    localStorage.setItem('salesWorkflowOrderDateFilterType', orderDateFilterType);
  }, [orderDateFilterType]);

  useEffect(() => {
    localStorage.setItem('salesWorkflowSelectedOrderMonth', selectedOrderMonth);
  }, [selectedOrderMonth]);

  useEffect(() => {
    localStorage.setItem('salesWorkflowSelectedOrderYear', selectedOrderYear);
  }, [selectedOrderYear]);

  // Process pending refreshes when dialogs close
  useEffect(() => {
    if (!isUserBusy) {
      processPendingRefreshes();
    }
  }, [editOrderDialogOpen, createOrderDialogOpen, createOfferDialogOpen, convertDialogOpen, reorderDialogOpen, createUpdateDialogOpen]);

  // Setup SSE connection for real-time updates
  useEffect(() => {
    let es = null;
    let reconnectTimeout = null;
    let pollingInterval = null;

    const connectSSE = async () => {
      try {
        const token = await getToken();
        
        // Create EventSource connection
        es = new EventSource(`${API_URL}/events?token=${token}`);
        
        es.onopen = () => {
          // Clear any polling fallback
          if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
          }
        };

        // Listen for enquiries changes
        es.addEventListener('enquiries_changed', (event) => {
          const data = JSON.parse(event.data);
          handleDataChange('enquiries', data.changedBy);
        });

        // Listen for offers changes
        es.addEventListener('offers_changed', (event) => {
          const data = JSON.parse(event.data);
          handleDataChange('offers', data.changedBy);
        });

        // Listen for orders changes
        es.addEventListener('orders_changed', (event) => {
          const data = JSON.parse(event.data);
          handleDataChange('orders', data.changedBy);
        });

        // Listen for follow-ups changes (triggered by enquiry/order changes)
        es.addEventListener('followups_changed', (event) => {
          const data = JSON.parse(event.data);
          handleDataChange('followups', data.changedBy);
        });

        es.onerror = (error) => {
          console.error('SSE connection error:', error);
          es.close();
          
          // Setup fallback polling
          if (!pollingInterval) {
            pollingInterval = setInterval(() => {
              if (canRefresh()) {
                loadTabData(tabValue, true);
              }
            }, 60000); // Poll every 60 seconds
          }
          
          // Attempt to reconnect after 5 seconds
          reconnectTimeout = setTimeout(() => {
            connectSSE();
          }, 5000);
        };

        setEventSource(es);
      } catch (error) {
        console.error('Error setting up SSE:', error);
      }
    };

    // Connect on mount
    connectSSE();

    // Cleanup on unmount
    return () => {
      if (es) {
        es.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Function to fetch offers data
  const fetchOffers = async () => {
    try {
      const token = await getToken();
      
      // Load offers from API
      const offersResponse = await fetch(`${API_URL}/offers`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (offersResponse.ok) {
        const offersData = await offersResponse.json();
        
        // Transform offers data for the sales workflow
        const transformedOffers = offersData.data?.map(offer => ({
            id: offer.id,
            businessName: offer.businessName,
            price: offer.totalPrice ? `$${parseFloat(offer.totalPrice).toFixed(2)}` : '$0.00',
            status: offer.status,
            offerDate: offer.offerDate ? formatDateForInput(offer.offerDate) : null,
            expiryDate: offer.expiryDate ? formatDateForInput(offer.expiryDate) : null,
            salesPersonName: offer.salesPersonName || 'Unassigned',
            organizationId: offer.organizationId,
            organizationName: offer.organizationName,
            offerSource: offer.offerSource,
            audiences: offer.audiences || [],
            contactPerson: offer.contactPerson,
            contactEmail: offer.contactEmail,
            contactPhone: offer.contactPhone,
            notes: offer.notes,
            designNotes: offer.designNotes,
            customerNotes: offer.customerNotes,
            imageCaption: offer.imageCaption || '',
            imageData: offer.imageData || '',
            createdAt: offer.createdAt,
            generatedUrl: offer.generatedUrl,
            generatedAt: offer.generatedAt,
            acceptedAt: offer.acceptedAt,
            acceptorName: offer.acceptorName || null,
            certificateUrl: offer.certificateUrl || null,
            changesRequestedAt: offer.changesRequestedAt
        })) || [];
        
        setOffers(transformedOffers);
        // Offers loaded successfully
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  // Separate fetch functions for each data type
  const fetchEnquiries = async () => {
    try {
      const token = await getToken();
      const enquiriesResponse = await fetch(`${API_URL}/enquiries`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (enquiriesResponse.ok) {
        const enquiriesData = await enquiriesResponse.json();
        
        const transformedEnquiries = enquiriesData.data?.map(enquiry => ({
          id: enquiry.id,
          businessName: enquiry.businessName,
          contactPerson: `${enquiry.firstName} ${enquiry.lastName}`,
          email: enquiry.email || '',
          phone: enquiry.phone || '',
          outcome: enquiry.outcome,
          status: enquiry.outcome,
          leadSource: enquiry.leadMethod,
          date: enquiry.date,
          notes: enquiry.salesNotes || enquiry.customerNotes || '',
          campaignType: enquiry.campaignType,
          advertiserInDatabase: enquiry.advertiserInDatabase,
          organizationId: enquiry.organizationId,
          firstName: enquiry.firstName,
          lastName: enquiry.lastName,
          salesNotes: enquiry.salesNotes,
          customerNotes: enquiry.customerNotes,
          followUpNotes: enquiry.followUpNotes,
          invoiceAmount: enquiry.invoiceAmount,
          followUpDate: enquiry.followUpDate,
          createdAt: enquiry.createdAt
        })) || [];
        
        setEnquiries(transformedEnquiries);
        return transformedEnquiries;
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    }
    return [];
  };

  const fetchOrders = async () => {
    try {
      const token = await getToken();
      const ordersResponse = await fetch(`${API_URL}/orders`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Also fetch update orders
      const updateOrdersResponse = await fetch(`${API_URL}/update-orders`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      let allOrders = [];
      
      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json();
        
        const transformedOrders = ordersData.data?.map(order => ({
          id: order.id,
          businessName: order.businessName || 'Unknown Business',
          status: order.status || 'New',
          value: order.totalPrice ? `$${parseFloat(order.totalPrice).toFixed(2)}` : '$0.00',
          salesPerson: order.salesPersonName || 'Unassigned',
          dateCreated: order.createdAt ? formatDateForInput(order.createdAt) : getCurrentDateForInput(),
          expectedClose: order.expectedCloseDate ? formatDateForInput(order.expectedCloseDate) : null,
          progress: getOrderProgress(order.status),
          orderType: order.orderType,
          startDate: order.startDate,
          endDate: order.endDate,
          totalValue: order.totalPrice || 0,
          notes: order.notes && order.notes !== 'undefined' ? order.notes : null,
          designNotes: order.designNotes && order.designNotes !== 'undefined' ? order.designNotes : null,
          createdAt: order.createdAt,
          updatedAt: order.updatedAt || null,
        })) || [];
        
        allOrders = [...transformedOrders];
      }
      
      // Add update orders to the list
      if (updateOrdersResponse.ok) {
        const updateOrdersData = await updateOrdersResponse.json();
        
        const transformedUpdateOrders = updateOrdersData.data?.map(order => ({
          id: order.id,
          businessName: order.organizationName || order.relatedOrderBusinessName || 'Unknown Business',
          status: order.status || 'New',
          value: order.totalPrice ? `$${parseFloat(order.totalPrice).toFixed(2)}` : '$0.00',
          salesPerson: order.salesPersonName || 'Unassigned',
          dateCreated: order.createdAt ? formatDateForInput(order.createdAt) : getCurrentDateForInput(),
          expectedClose: null,
          progress: getOrderProgress(order.status),
          orderType: 'update',
          // Use dates from the related order (fetched via subquery)
          startDate: order.relatedOrderStartDate || null,
          endDate: order.relatedOrderEndDate || order.newEndDate || null,
          totalValue: order.totalPrice || 0,
          notes: order.notes && order.notes !== 'undefined' ? order.notes : null,
          designNotes: null,
          createdAt: order.createdAt,
          updatedAt: order.updatedAt || null,
          orderUpdateType: order.orderUpdateType,
          relatedOrderId: order.relatedOrderId
        })) || [];
        
        allOrders = [...allOrders, ...transformedUpdateOrders];
      }
      
      setOrders(allOrders);
      return allOrders;
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
    return [];
  };

  // Fetch non-current follow-ups (lapsed orgs < 2 years old) for the Follow Ups tab
  const fetchNonCurrentFollowUps = async () => {
    setNonCurrentLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/outreach/non-current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setNonCurrentFollowUps(data.data || []);
    } catch (err) {
      console.error('Error fetching non-current follow-ups:', err);
    } finally {
      setNonCurrentLoading(false);
    }
  };

  const generateFollowUps = (enquiriesData, ordersData, outreachData) => {
    // Use freshly-passed data when available; otherwise fall back to whatever
    // was cached in the ref the last time the Follow-Ups tab was opened.
    const outreachToUse = Array.isArray(outreachData)
      ? outreachData
      : outreachFollowUpsCacheRef.current;
    const allFollowUps = [];
    const today = new Date();
    
    // Enquiry follow-ups
    const enquiryFollowUps = enquiriesData.filter(enquiry => {
      return enquiry.status === 'Follow Up Later';
    }).map(enquiry => ({
      id: `enquiry-${enquiry.id}`,
      type: 'Enquiry Follow-Up',
      businessName: enquiry.businessName,
      contactPerson: enquiry.contactPerson,
      dueDate: enquiry.followUpDate || new Date(),
      priority: enquiry.followUpDate && new Date(enquiry.followUpDate) < today ? 'High' : 'Medium',
      notes: `Follow up enquiry: ${enquiry.followUpNotes || enquiry.notes || 'No additional notes'}`,
      source: 'enquiry',
      sourceId: enquiry.id,
      salesNotes: enquiry.salesNotes,
      customerNotes: enquiry.customerNotes,
      followUpNotes: enquiry.followUpNotes
    }));
    
    // Order renewal follow-ups
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    const orderFollowUps = ordersData.filter(order => {
      // Only include original ad orders, not update orders (artwork changes, date extensions, etc.)
      if (order.orderType === 'update') return false;
      if (order.endDate) {
        const endDate = new Date(order.endDate);
        return endDate >= today && endDate <= nextMonth;
      }
      return false;
    }).map(order => ({
      id: `order-${order.id}`,
      type: 'Order Renewal',
      businessName: order.businessName || 'Unknown Business',
      contactPerson: order.contactPerson || 'Contact from order',
      dueDate: order.endDate,
      priority: 'Medium',
      notes: `Order #${order.id} ends on ${formatDate(order.endDate)} - potential renewal opportunity`,
      source: 'order',
      sourceId: order.id,
      orderValue: order.totalValue || 'Unknown'
    }));

    // Outreach contacts marked "Follow Up Later"
    const outreachFollowUpItems = outreachToUse.map(contact => ({
      id: `outreach-${contact.id}`,
      type: 'Outreach Follow-Up',
      businessName: contact.business_name,
      contactPerson: contact.contact_name || '',
      dueDate: contact.follow_up_date || new Date().toISOString(),
      priority: contact.follow_up_date && new Date(contact.follow_up_date) < today ? 'High' : 'Medium',
      notes: contact.notes || 'No notes added.',
      source: 'outreach',
      sourceId: contact.id,
    }));
    
    allFollowUps.push(...enquiryFollowUps, ...orderFollowUps, ...outreachFollowUpItems);
    allFollowUps.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    setFollowUps(allFollowUps);
    return allFollowUps;
  };

  // Load data for a specific tab
  const loadTabData = async (tab, forceRefresh = false) => {
    // Check if data is already cached and we're not forcing a refresh
    if (dataCache[tab] && !forceRefresh) {
      return;
    }

    setTabLoading(prev => ({ ...prev, [tab]: true }));
    
    try {
      let enquiriesData = enquiries;
      let ordersData = orders;
      let offersData = offers;

      switch(tab) {
        case 0: // Enquiries
          enquiriesData = await fetchEnquiries();
          // Generate follow-ups since they depend on enquiries
          if (ordersData.length > 0) {
            generateFollowUps(enquiriesData, ordersData);
          }
          break;
        case 1: // Offers
          await fetchOffers();
          break;
        case 2: // Orders
          ordersData = await fetchOrders();
          // Generate follow-ups since they depend on orders
          if (enquiriesData.length > 0) {
            generateFollowUps(enquiriesData, ordersData);
          }
          break;
        case 3: // Follow Ups
          // Follow-ups depend on both enquiries and orders
          if (!dataCache[0]) {
            enquiriesData = await fetchEnquiries();
          }
          if (!dataCache[2]) {
            ordersData = await fetchOrders();
          }
          // Also pull in outreach contacts marked "Follow Up Later"
          try {
            const outreachToken = await getToken();
            const outreachRes = await fetch(
              `${API_URL}/outreach/contacts?status=follow_up&limit=500`,
              { headers: { Authorization: `Bearer ${outreachToken}` } }
            );
            const outreachJson = await outreachRes.json();
            const outreachContacts = outreachJson.success ? (outreachJson.data || []) : [];
            outreachFollowUpsCacheRef.current = outreachContacts; // persist for tab-switch calls
            generateFollowUps(enquiriesData, ordersData, outreachContacts);
          } catch {
            generateFollowUps(enquiriesData, ordersData);
          }
          fetchNonCurrentFollowUps();
          break;
      }

      // Mark tab as cached
      setDataCache(prev => ({ ...prev, [tab]: true }));
    } catch (error) {
      console.error(`Error loading tab ${tab} data:`, error);
    } finally {
      setTabLoading(prev => ({ ...prev, [tab]: false }));
    }
  };

  // Listen for real-time order_updated events from the server (triggered by Ad Orders page)
  // and immediately patch status + updatedAt in local state so the timestamp column stays live.
  useEffect(() => {
    if (!socket) return;
    const handleOrderUpdated = ({ id, order }) => {
      if (!id || !order) return;
      setOrders(prev => prev.map(o =>
        o.id === id
          ? { ...o, status: order.status ?? o.status, updatedAt: order.updatedAt ?? o.updatedAt }
          : o
      ));
    };
    socket.on('order_updated', handleOrderUpdated);
    return () => socket.off('order_updated', handleOrderUpdated);
  }, [socket]);

  // Legacy function for backward compatibility - now loads only active tab.
  // Always force-refreshes tab 2 (orders) as well so timestamps stay accurate.
  const loadSalesData = async () => {
    setLoading(true);
    try {
      await loadTabData(tabValue, true);
      // Always keep order data fresh regardless of active tab
      if (tabValue !== 2) {
        await loadTabData(2, true);
      }
    } catch (error) {
      console.error('Error loading sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is currently busy (editing, creating, or typing)
  const canRefresh = () => {
    return (
      !editOrderDialogOpen &&
      !createOrderDialogOpen &&
      !createOfferDialogOpen &&
      !convertDialogOpen &&
      !reorderDialogOpen &&
      !createUpdateDialogOpen &&
      document.activeElement.tagName !== 'INPUT' &&
      document.activeElement.tagName !== 'TEXTAREA' &&
      document.activeElement.tagName !== 'SELECT'
    );
  };

  // Queue a refresh for later if user is busy
  const queueRefresh = (type) => {
    if (!pendingRefreshes.includes(type)) {
      setPendingRefreshes(prev => [...prev, type]);
    }
  };

  // Process pending refreshes when user becomes available
  const processPendingRefreshes = async () => {
    if (pendingRefreshes.length > 0 && canRefresh()) {
      const refreshTypes = [...pendingRefreshes];
      setPendingRefreshes([]);
      
      // Determine which tab needs refresh
      const tabMap = {
        'enquiries': 0,
        'offers': 1,
        'orders': 2,
        'followups': 3
      };
      
      // Refresh the current tab if it's affected
      for (const type of refreshTypes) {
        const affectedTab = tabMap[type];
        if (affectedTab === tabValue) {
          await loadTabData(tabValue, true);
          break;
        }
      }
    }
  };

  // Handle real-time data change notifications
  const handleDataChange = async (type, changedBy) => {
    // Map event type to tab index
    const tabMap = {
      'enquiries': 0,
      'offers': 1,
      'orders': 2,
      'followups': 3
    };
    
    const affectedTab = tabMap[type];
    
    // Only refresh if we're on the affected tab
    if (affectedTab !== tabValue) {
      // Invalidate cache for that tab so it refreshes next time
      setDataCache(prev => ({ ...prev, [affectedTab]: false }));
      return;
    }
    
    // Check if we can refresh now
    if (canRefresh()) {
      await loadTabData(affectedTab, true);
      
      // Show subtle notification
      if (changedBy && changedBy !== user?.name) {
        setSnackbar({
          open: true,
          message: `Data updated by ${changedBy}`,
          severity: 'info'
        });
      }
    } else {
      // Queue for later
      queueRefresh(type);
    }
  };

  // Calculate cost for date extension
  const calculateDateExtensionCost = (group, oldEndDate, newEndDate) => {
    // This would typically call pricing service
    // For now, return a simple calculation based on duration
    const oldDate = new Date(oldEndDate);
    const newDate = new Date(newEndDate);
    const additionalDays = Math.ceil((newDate - oldDate) / (1000 * 60 * 60 * 24));
    const additionalWeeks = Math.ceil(additionalDays / 7);
    
    // Example: $10 per week extension
    return additionalWeeks * 10;
  };

  // Handle update type selection toggle
  const handleToggleUpdateType = (type) => {
    setSelectedUpdateTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Handle comprehensive update submission
  const handleComprehensiveUpdateSubmit = async (updateData, artworkFiles) => {
    try {
      setLoading(true);
      const token = await getToken();

      // First, create the update order without attachments
      const finalUpdateData = {
        ...updateData,
        salesPersonId: user.id
      };

      // Create the update order
      const response = await fetch(`${API_URL}/update-orders/comprehensive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(finalUpdateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create update order');
      }

      const responseData = await response.json();
      const orderId = responseData.data?.id;
      const relatedOrderId = updateData.relatedOrderId;

      // If there are artwork files, upload them after the order is created
      if (artworkFiles && artworkFiles.length > 0 && orderId) {
        const formData = new FormData();
        artworkFiles.forEach((attachment) => {
          formData.append('attachments', attachment.file);
        });

        // Upload to the update order
        const uploadResponse = await fetch(`${API_URL}/order-attachments/${orderId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          console.error('Failed to upload attachments to update order');
        } else if (relatedOrderId) {
          // Also upload to the original order
          const originalFormData = new FormData();
          artworkFiles.forEach((attachment) => {
            originalFormData.append('attachments', attachment.file);
          });

          const originalUploadResponse = await fetch(`${API_URL}/order-attachments/${relatedOrderId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: originalFormData
          });

          if (!originalUploadResponse.ok) {
            console.error('Failed to upload attachments to original order');
          }
        }
      }

      // If artwork was supplied by the client, skip proof and go straight to invoice
      const artworkUpdate = updateData.updates?.find(u => u.type === 'artwork');
      const isArtworkSupplied = artworkUpdate?.artworkCostType === 'artwork_supplied';

      setSnackbar({
        open: true,
        message: isArtworkSupplied
          ? 'Update order created — artwork supplied, opening invoice...'
          : 'Update order created successfully!',
        severity: 'success'
      });

      // Reset and close dialog
      handleCloseUpdateDialog();
      
      // Refresh data
      await loadSalesData();

      // Navigate to invoice creation immediately when artwork is supplied
      if (isArtworkSupplied && orderId) {
        navigate(`/app/invoice/create/${orderId}`);
        return;
      }
    } catch (error) {
      console.error('Error creating update order:', error);
      setSnackbar({
        open: true,
        message: `Error creating update: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Close and reset update dialog
  const handleCloseUpdateDialog = () => {
    setCreateUpdateDialogOpen(false);
    setSelectedOrderForUpdate(null);
    setSelectedUpdateTypes({ dates: false, link: false, artwork: false });
    setSelectedGroupForDateUpdate(null);
    setNewEndDate('');
    setNewDuration('');
    setDateUpdateCost(0);
    setDateUpdateNoCost(false);
    setSelectedGroupForLinkUpdate(null);
    setLinkUpdateType('');
    setLinkUpdateContent('');
    setArtworkAttachments([]);
    setArtworkChangeCount(0);
    setArtworkUpdateNoCost(false);
    setUpdateNotes('');
  };


  const getOrderProgress = (status) => {
    const progressMap = {
      'New': 10,
      'Waiting for Artwork': 25,
      'In Design': 50,
      'Proofing': 75,
      'Approved': 90,
      'Uploaded': 95,
      'Invoiced': 100,
      'Cancelled': 0
    };
    return progressMap[status] || 10;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    localStorage.setItem('salesWorkflowActiveTab', newValue.toString());
  };

  // Load organizations for the organization picker
  const loadOrganizations = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/organizations?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Handle both old format (array) and new format (object with data property)
        if (result.success && result.data) {
          setOrganizations(result.data);
        } else if (Array.isArray(result)) {
          setOrganizations(result);
        } else {
          setOrganizations([]);
        }
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      setOrganizations([]);
    }
  };



  // Handle outcome change from EnquiryActions component
  const handleEnquiryOutcomeChange = async (enquiryId, newOutcome, additionalData = {}) => {
    try {
      const token = await getToken();
      const updateData = { 
        outcome: newOutcome,
        ...additionalData
      };

      const response = await fetch(`${API_URL}/enquiries/${enquiryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        // Update local state
        setEnquiries(prev => prev.map(enq => 
          enq.id === enquiryId 
            ? { ...enq, outcome: newOutcome, ...additionalData } 
            : enq
        ));
        
        setSnackbar({ 
          open: true, 
          message: `Enquiry updated to ${newOutcome} successfully!`, 
          severity: 'success' 
        });
        
        // Reload data to ensure consistency
        loadSalesData();
      } else {
        throw new Error('Failed to update enquiry');
      }
    } catch (error) {
      console.error('Error updating enquiry:', error);
      setSnackbar({ 
        open: true, 
        message: `Error updating enquiry: ${error.message}`, 
        severity: 'error' 
      });
    }
  };



  // Handle follow-up date change for enquiries
  const handleFollowUpDateChange = async (enquiryId, newDate) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/enquiries/${enquiryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ followUpDate: newDate })
      });

      if (response.ok) {
        // Update local state
        setEnquiries(prev => prev.map(enq => 
          enq.id === enquiryId ? { ...enq, followUpDate: newDate } : enq
        ));
        setSnackbar({ 
          open: true, 
          message: 'Follow-up date updated successfully!', 
          severity: 'success' 
        });
      } else {
        throw new Error('Failed to update follow-up date');
      }
    } catch (error) {
      console.error('Error updating follow-up date:', error);
      setSnackbar({ 
        open: true, 
        message: `Error updating follow-up date: ${error.message}`, 
        severity: 'error' 
      });
    }
  };

  // Handle opening the convert to business dialog
  const handleConvertToBusiness = (enquiry) => {
    setEnquiryToConvert(enquiry);
    setOrganizationFormData({
      name: enquiry.businessName || '',
      business_type: '', // Don't autofill business type
      primary_name: enquiry.firstName && enquiry.lastName ? `${enquiry.firstName} ${enquiry.lastName}` : '',
      primary_email: enquiry.email || '',
      primary_phone: enquiry.phone || '',
      generalEmail: enquiry.email || '',
      generalPhone: enquiry.phone || '',
      address: '',
      suburb: '',
      state: '',
      postcode: '',
      notes: `Converted from enquiry: ${enquiry.leadMethod}`,
      is_business: true,
      is_school: false,
      is_active: true,
      sales_consultant_id: user.id // Default to current user
    });
    setConvertDialogOpen(true);
  };

  // Handle reorder action for order renewals
  const handleReorder = async (followUp) => {
    
    try {
      setLoading(true);
      const token = await getToken();
      
      // Fetch the original order data
      const response = await fetch(`${API_URL}/orders/${followUp.sourceId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch original order data');
      }
      
      const originalOrderResponse = await response.json();
      const originalOrder = originalOrderResponse.data || originalOrderResponse;
      
      // Calculate new dates based on the original order's end date and duration
      let newStartDate, newEndDate;
      
      if (originalOrder.audiences && originalOrder.audiences.length > 0) {
        // Use the latest end date from audiences
        const latestEndDate = originalOrder.audiences.reduce((latest, audience) => {
          if (!audience.endDate) return latest;
          const endDate = new Date(audience.endDate);
          return !latest || endDate > latest ? endDate : latest;
        }, null);
        
        if (latestEndDate) {
          // Start date is the day after the previous order's end date
          newStartDate = new Date(latestEndDate);
          newStartDate.setDate(newStartDate.getDate() + 1);
          
          // Calculate duration from the original order
          const originalStartDate = new Date(originalOrder.audiences[0].startDate);
          const durationMs = latestEndDate - originalStartDate;
          
          // Apply the same duration to the new order
          newEndDate = new Date(newStartDate.getTime() + durationMs);
        }
      }
      
      // Fallback to using order-level endDate if available
      if (!newStartDate && originalOrder.endDate) {
        const orderEndDate = new Date(originalOrder.endDate);
        newStartDate = new Date(orderEndDate);
        newStartDate.setDate(newStartDate.getDate() + 1);
        
        // Default to 1 year duration if we can't calculate original duration
        newEndDate = new Date(newStartDate);
        newEndDate.setFullYear(newEndDate.getFullYear() + 1);
      }
      
      // Final fallback
      if (!newStartDate) {
        newStartDate = new Date();
        newEndDate = new Date();
        newEndDate.setFullYear(newEndDate.getFullYear() + 1);
      }
      
      // Fetch organization details to get the proper business name
      let organizationName = originalOrder.businessName;
      const organizationId = originalOrder.organization || originalOrder.organizationId;
      
      if (organizationId) {
        try {
          const orgResponse = await fetch(`${API_URL}/organizations/${organizationId}`, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (orgResponse.ok) {
            const orgData = await orgResponse.json();
            organizationName = orgData.name || originalOrder.businessName;
          }
        } catch (orgError) {
          console.warn('Could not fetch organization details, using order business name:', orgError);
        }
      }
      
      // Prepare the reorder data for the form and API
      const reorderData = {
        // API fields (for submission)
        orderType: 'ad',
        organization: organizationId, // API expects this field
        organization_id: organizationId, // Also set this for compatibility
        productType: 'Repeat',  // Default to Repeat for reorders
        product_type: 'Repeat',
        businessName: organizationName,
        business_name: organizationName, // Form expects business_name
        businessNumber: organizationId ? organizationId.toString() : '',
        business_number: organizationId ? organizationId.toString() : '',
        businessType: originalOrder.businessType || '',
        business_type: originalOrder.businessType || '',
        notes: `Reorder from Order #${followUp.sourceId} - ${new Date().toLocaleDateString()}`,
        designNotes: originalOrder.designNotes || '',
        design_notes: originalOrder.designNotes || '',
        salesPersonId: originalOrder.salesPersonId || user?.id,
        status: 'New', // Set initial status
        audiences: originalOrder.audiences && Array.isArray(originalOrder.audiences) ? originalOrder.audiences.map(audience => ({
          ...audience,
          organization_id: audience.organization || audience.organization_id, // Ensure org ID is set
          startDate: newStartDate.toISOString().split('T')[0],
          endDate: newEndDate.toISOString().split('T')[0]
        })) : [],
        // Store locked organization info for the form
        lockedOrganization: {
          id: organizationId,
          name: organizationName
        }
      };
      
      // Set the reorder form data and open the dialog
      setReorderFormData({
        ...reorderData,
        audiences: reorderData.audiences || [] // Ensure audiences is always an array
      });
      setReorderDialogOpen(true);
      
      setSnackbar({
        open: true,
        message: `Order form opened with data from Order #${followUp.sourceId}`,
        severity: 'info'
      });
      
    } catch (error) {
      console.error('Error preparing reorder:', error);
      setReorderDialogOpen(false); // Close dialog on error
      setSnackbar({
        open: true,
        message: `Error preparing reorder: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle audience changes for reorder form
  const handleReorderAudienceChange = (index, field, value) => {
    const updatedAudiences = [...reorderFormData.audiences];
    updatedAudiences[index] = { ...updatedAudiences[index], [field]: value };
    setReorderFormData({ ...reorderFormData, audiences: updatedAudiences });
  };

  // Add audience for reorder form
  const addReorderAudience = () => {
    const newAudience = {
      organization_id: '',
      channel: '',
      adType: '',
      adGroup: reorderFormData.audiences.length + 1,
      startDate: getCurrentDateForInput(),
      endDate: getFutureDateForInput(365), // Default 1 year from now
      price: '',
      discount: '0',
      discountType: '$',
      eLinkType: 'None',
      eLinkContent: ''
    };
    setReorderFormData({
      ...reorderFormData,
      audiences: [...reorderFormData.audiences, newAudience]
    });
  };

  // Remove audience for reorder form
  const removeReorderAudience = (index) => {
    const updatedAudiences = reorderFormData.audiences.filter((_, i) => i !== index);
    setReorderFormData({ ...reorderFormData, audiences: updatedAudiences });
  };

  // Handle reorder form submission
  const handleReorderSubmit = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      // Filter out frontend-only fields that shouldn't be sent to the API
      const { tempAttachments, ...apiData } = reorderFormData;
      
      const response = await fetch(`${API_URL}/ad-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Server error response:', errorData);
        throw new Error(`Failed to create reorder: ${response.status} ${response.statusText}`);
      }
      
      const newOrder = await response.json();
      
      setSnackbar({
        open: true,
        message: `New order #${newOrder.orderId || newOrder.id} created successfully!`,
        severity: 'success'
      });
      
      // Close dialog and refresh data - force fresh order fetch so follow-ups regenerate correctly
      setReorderDialogOpen(false);
      setReorderFormData({});
      // Clear the orders cache so fetchOrders() runs fresh when generating follow-ups
      setDataCache(prev => ({ ...prev, [2]: false }));
      await loadSalesData();
      
    } catch (error) {
      console.error('Error creating reorder:', error);
      setSnackbar({
        open: true,
        message: `Error creating reorder: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle creating reoffer from follow-up
  const handleReoffer = async (followUp) => {
    
    try {
      setLoading(true);
      const token = await getToken();
      
      // Fetch the original order data
      const response = await fetch(`${API_URL}/orders/${followUp.sourceId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch original order data');
      }
      
      const originalOrderResponse = await response.json();
      const originalOrder = originalOrderResponse.data || originalOrderResponse;
      
      // Fetch organization details to get the proper business name
      let organizationName = originalOrder.businessName;
      const organizationId = originalOrder.organization || originalOrder.organizationId;
      
      if (organizationId) {
        try {
          const orgResponse = await fetch(`${API_URL}/organizations/${organizationId}`, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (orgResponse.ok) {
            const orgData = await orgResponse.json();
            organizationName = orgData.name || originalOrder.businessName;
          }
        } catch (orgError) {
          console.warn('Could not fetch organization details, using order business name:', orgError);
        }
      }
      
      // Calculate duration from the original order to maintain same duration
      let durationDays = 365; // Default 1 year
      if (originalOrder.audiences && originalOrder.audiences.length > 0) {
        const firstAudience = originalOrder.audiences[0];
        if (firstAudience.startDate && firstAudience.endDate) {
          const start = new Date(firstAudience.startDate);
          const end = new Date(firstAudience.endDate);
          durationDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        }
      }
      
      // Map order data to offer format with same details
      const defaultOfferData = {
        businessType: organizationId ? 'existing' : 'new',
        organization_id: organizationId || '',
        business_name: organizationName || '',
        notes: `Reoffer from Order #${followUp.sourceId} - ${new Date().toLocaleDateString()}\n${originalOrder.notes || ''}`,
        offerNotes: '',
        designNotes: originalOrder.designNotes || '',
        // Use same audiences with updated dates but preserve all other details
        audiences: originalOrder.audiences?.map((audience, index) => ({
          organization_id: audience.organizationId || audience.organization_id || '',
          channel: audience.channel || '',
          adType: audience.adType || audience.ad_type || '',
          adGroup: audience.adGroup || audience.ad_group || index + 1,
          startDate: getCurrentDateForInput(), // Start today
          endDate: getFutureDateForInput(durationDays), // Same duration as original
          price: audience.price?.toString() || '',
          discount: audience.discount?.toString() || '0',
          discountType: audience.discountType || audience.discount_type || '$',
          // Preserve addons if they exist
          addons: audience.addons || {}
        })) || [{
          organization_id: '',
          channel: '',
          adType: '',
          adGroup: 1,
          startDate: getCurrentDateForInput(),
          endDate: getFutureDateForInput(durationDays),
          price: '',
          discount: '0',
          discountType: '$',
          addons: {}
        }],
        // Store locked organization info for the form
        lockedOrganization: organizationId ? {
          id: organizationId,
          name: organizationName
        } : null
      };

      setOfferFormData(currentFormData => ({ ...currentFormData, ...defaultOfferData }));
      setCreateOfferDialogOpen(true);
      setIsEditingOffer(false);
      setEditingOfferId(null);
      
      setSnackbar({
        open: true,
        message: `Offer form opened with data from Order #${followUp.sourceId}`,
        severity: 'info'
      });
      
    } catch (error) {
      console.error('Error creating reoffer:', error);
      setSnackbar({
        open: true,
        message: `Error creating reoffer: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open the Create Offer dialog pre-filled for a non-current follow-up org
  const handleCreateOfferForNonCurrent = (org) => {
    setOfferFormData(prev => ({
      ...prev,
      businessType: 'existing',
      organization_id: org.id,
      business_name: org.name || '',
      offer_source: 'manual',
    }));
    setIsEditingOffer(false);
    setEditingOfferId(null);
    setCreateOfferDialogOpen(true);
    setTabValue(1); // switch to Offers tab so the dialog is in context
  };

  // Add a non-current org to the outreach Contact History
  const handleAddNonCurrentToOutreach = async (org) => {
    try {
      const token = await getToken();
      const { fetchCSRFToken } = await import('../utils/csrfProtection');
      const csrf = await fetchCSRFToken();
      const res = await fetch(`${API_URL}/outreach/contacts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrf,
        },
        body: JSON.stringify({
          business_name: org.name,
          contact_email: org.primaryEmail || '',
          contact_phone: org.primaryPhone || '',
          suburb: org.suburb || '',
          state: org.state || '',
          organization_id: org.id,
          status: 'contacted',
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSnackbar({ open: true, message: `${org.name} added to Business Outreach`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to add to outreach', severity: 'error' });
    }
  };

  // Handle opening notes dialog for any entity
  const handleOpenNotesDialog = (entityType, entityId, title, existingNotes = [], defaultNoteType = 'general') => {
    setNotesDialog({
      open: true,
      title,
      entityType,
      entityId,
      defaultNoteType,
      notes: existingNotes
    });
  };

  // Handle closing notes dialog
  const handleCloseNotesDialog = () => {
    setNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: '',
      defaultNoteType: 'general',
      notes: []
    });
    
    // Just close the dialog - the notes are already saved and UI updated
    // No need to force refresh as this was causing flickering

  };

  // Handle opening order notes dialog (for Order Tracking tab)
  const handleOpenOrderNotesDialog = (entityType, entityId, title, existingNotes = []) => {
    setOrderNotesDialog({
      open: true,
      title,
      entityType,
      entityId,
      notes: existingNotes
    });
  };

  // Handle closing order notes dialog
  const handleCloseOrderNotesDialog = () => {
    setOrderNotesDialog({
      open: false,
      title: '',
      entityType: '',
      entityId: '',
      notes: []
    });
  };

  // Handle order notes update from the dialog - update local state
  const handleOrderNotesUpdate = async (updatedNotes, newNote, options = {}) => {
    
    // Update the relevant local state to trigger UI refresh
    if (orderNotesDialog.entityType === 'order') {
      setOrders(prev => prev.map(order => 
        order.id.toString() === orderNotesDialog.entityId 
          ? { 
              ...order, 
              notes: order.notes || 'Has notes',
              hasLocalStorageNotes: true
            }
          : order
      ));
    }
    
    // Only reload data if this was a successful backend save (not localStorage-only)
    if (!options.localOnly) {
      try {
        await loadSalesData();
      } catch (error) {
      }
    }
  };

  // Helper function to get total note count including localStorage
  const getTotalNoteCount = (entity, entityType) => {
    // Count database notes
    let dbNoteCount = 0;
    if (entityType === 'enquiry') {
      if (entity.salesNotes) dbNoteCount++;
      if (entity.customerNotes) dbNoteCount++;
      if (entity.followUpNotes) dbNoteCount++;
    } else if (entityType === 'offer' || entityType === 'order') {
      if (entity.notes) dbNoteCount++;
      if (entity.designNotes) dbNoteCount++;
    }
    
    // Count localStorage notes
    // For follow-up entities, use the sourceId to match the original enquiry's localStorage notes
    const entityId = entity.sourceId || entity.id;
    const storageKey = `notes_${entityType}_${entityId}`;
    const storageNotes = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    const totalCount = dbNoteCount + storageNotes.length;
    

    
    return totalCount;
  };

  // Handle notes update from the dialog - update local state
  const handleNotesUpdate = async (updatedNotes, newNote, options = {}) => {
    
    // Update the relevant local state to trigger UI refresh
    if (notesDialog.entityType === 'enquiry') {
      setEnquiries(prev => prev.map(enq => 
        enq.id.toString() === notesDialog.entityId 
          ? { 
              ...enq, 
              salesNotes: enq.salesNotes || 'Has notes',
              // Mark that this enquiry has localStorage notes for getTotalNoteCount
              hasLocalStorageNotes: true
            }
          : enq
      ));
    } else if (notesDialog.entityType === 'offer') {
      setOffers(prev => prev.map(offer => 
        offer.id.toString() === notesDialog.entityId 
          ? { 
              ...offer, 
              notes: offer.notes || 'Has notes',
              hasLocalStorageNotes: true
            }
          : offer
      ));
    } else if (notesDialog.entityType === 'order') {
      setOrders(prev => prev.map(order => 
        order.id.toString() === notesDialog.entityId 
          ? { 
              ...order, 
              notes: order.notes || 'Has notes',
              hasLocalStorageNotes: true
            }
          : order
      ));
    }
    
    setSnackbar({ 
      open: true, 
      message: 'Note added successfully!', 
      severity: 'success' 
    });
    
    // Only reload data if this was a successful backend save (not localStorage-only)
    if (!options.localOnly) {
      try {
        await loadSalesData();
      } catch (error) {
      }
    } else {
    }
  };

  // Handle creating organization from enquiry
  const handleCreateOrganization = async (orgData) => {
    try {
      const token = await getToken();
      
      // Transform the form data to match API expectations
      const organizationPayload = {
        name: orgData.name,
        primaryName: orgData.primary_name,
        primaryEmail: orgData.primary_email,
        primaryPhone: orgData.primary_phone,
        primaryGeneralEmail: orgData.primary_general_email,
        accountsName: orgData.accounts_name,
        accountsEmail: orgData.accounts_email,
        accountsPhone: orgData.accounts_phone,
        accountsGeneralEmail: orgData.accounts_general_email,
        principalName: orgData.principal_name,
        principalEmail: orgData.principal_email,
        principalPhone: orgData.principal_phone,
        principalGeneralEmail: orgData.principal_general_email,
        businessManagerName: orgData.business_manager_name,
        businessManagerEmail: orgData.business_manager_email,
        businessManagerPhone: orgData.business_manager_phone,
        businessManagerGeneralEmail: orgData.business_manager_general_email,
        address: orgData.address,
        formattedAddress: orgData.formattedAddress,
        latitude: orgData.latitude,
        longitude: orgData.longitude,
        state: orgData.state,
        suburb: orgData.suburb,
        greaterRegion: orgData.greaterRegion,
        subRegion: orgData.subRegion,
        postcode: orgData.postcode,
        generalEmail: orgData.generalEmail,
        generalPhone: orgData.generalPhone,
        website: orgData.website,
        sameAsStreetAddress: orgData.sameAsStreetAddress,
        postalAddress: orgData.postalAddress,
        businessType: orgData.business_type,
        paymentTerms: orgData.payment_terms,
        notes: orgData.notes,
        badDebt: orgData.bad_debt,
        businessClosed: orgData.business_closed,
        isActive: orgData.is_active,
        isSchool: orgData.is_school,
        isBusiness: orgData.is_business,
        salesConsultantId: orgData.sales_consultant_id,
        numberOfFamilies: orgData.numberOfFamilies,
        schoolStatus: orgData.schoolStatus,
        schoolType: orgData.schoolType,
        paymentModel: orgData.paymentModel,
        adsEnabled: orgData.adsEnabled,
        hasSchoolLink: orgData.hasSchoolLink,
        hasEPublisher: orgData.hasEPublisher,
        hasPaper: orgData.hasPaper,
        lastInvoiceDate: orgData.lastInvoiceDate,
        agreementStartDate: orgData.agreementStartDate,
        agreementEndDate: orgData.agreementEndDate,
        schoolLinkSubscribers: orgData.schoolLinkSubscribers,
        activeSchoolLinkAds: orgData.activeSchoolLinkAds,
        deploymentsPerTerm: orgData.deploymentsPerTerm,
        ePublisherSubscribers: orgData.ePublisherSubscribers,
        ePublisherCost: orgData.ePublisherCost,
        activeEPublisherAds: orgData.activeEPublisherAds,
        paperCost: orgData.paperCost,
        paperType: orgData.paperType,
        activePaperAds: orgData.activePaperAds,
        advertisingObjectives: orgData.advertisingObjectives,
        businessStatus: orgData.businessStatus,
        additionalContacts: orgData.additionalContacts || []
      };
      
      const response = await fetch(`${API_URL}/organizations`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(organizationPayload)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Check if this is an enquiry or offer conversion
        const isOfferConversion = enquiryToConvert && !enquiryToConvert.advertiserInDatabase;
        
        if (isOfferConversion) {
          // This is an offer conversion - update the offer
          const updateResponse = await fetch(`${API_URL}/offers/${enquiryToConvert.id}`, {
            method: 'PUT',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              business_type: 'existing',
              organization_id: result.organizationId,
              business_name: orgData.name
            })
          });

          if (updateResponse.ok) {
            // Update local offers state
            setOffers(prev => prev.map(offer => 
              offer.id === enquiryToConvert.id 
                ? { 
                    ...offer, 
                    businessType: 'existing',
                    organizationId: result.organizationId,
                    businessName: orgData.name,
                    organizationName: orgData.name
                  } 
                : offer
            ));

            // If this offer is currently being edited, update the form data
            if (isEditingOffer && editingOfferId === enquiryToConvert.id) {
              setOfferFormData(prev => ({
                ...prev,
                businessType: 'existing',
                organization_id: result.organizationId,
                business_name: orgData.name
              }));
            }
          }
        } else {
          // This is an enquiry conversion - update the enquiry
          const updateResponse = await fetch(`${API_URL}/enquiries/${enquiryToConvert.id}`, {
            method: 'PUT',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              advertiserInDatabase: true,
              organizationId: result.organizationId
            })
          });

          // Update local enquiries state
          setEnquiries(prev => prev.map(enq => 
            enq.id === enquiryToConvert.id 
              ? { 
                  ...enq, 
                  advertiserInDatabase: 'Yes', 
                  organizationId: result.organizationId,
                  linkedOrganizationName: orgData.name
                } 
              : enq
          ));
        }

        setConvertDialogOpen(false);
        setEnquiryToConvert(null);
        resetOrganizationFormData();
        
        // Add the new organization to the local organizations list
        const newOrganization = {
          id: result.organizationId,
          name: orgData.name,
          businessType: orgData.business_type,
          isBusiness: orgData.is_business,
          isSchool: orgData.is_school,
          isActive: orgData.is_active
        };
        setOrganizations(prev => [...prev, newOrganization]);
        
        setSnackbar({ 
          open: true, 
          message: `Business "${organizationFormData.name}" created successfully!${isOfferConversion ? ' Offer updated to use existing business.' : ''}`, 
          severity: 'success' 
        });
        
        // Reload organizations and sales data
        loadOrganizations();
        loadSalesData();
        
        // If this was an offer conversion, refresh the offers list
        if (isOfferConversion) {
          await fetchOffers();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create business');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      setSnackbar({ 
        open: true, 
        message: `Error creating business: ${error.message}`, 
        severity: 'error' 
      });
    }
  };



  // Handle creating offer from enquiry
  const handleCreateOfferFromEnquiry = (enquiry) => {
    // Collect categorized notes from the enquiry (database + localStorage)
    const categorizedNotes = collectCategorizedNotesFromEnquiry(enquiry);
    // Set offer form data with enquiry information
    const defaultOfferData = {
      businessType: enquiry.organizationId ? 'existing' : 'new',
      organization_id: enquiry.organizationId || '',
      business_name: enquiry.businessName || '',
      offer_source: 'enquiry', // Track that this offer came from an enquiry
      enquiry_id: enquiry.id, // Store enquiry ID for archiving later
      // Store contact information from enquiry
      contact_person: `${enquiry.firstName || ''} ${enquiry.lastName || ''}`.trim(),
      contact_email: enquiry.email || '',
      contact_phone: enquiry.phone || '',
      // Pass categorized notes from enquiry
      salesNotesFromEnquiry: categorizedNotes.salesNotes.join('\n\n') || '',
      designNotesFromEnquiry: categorizedNotes.designNotes.join('\n\n') || '',
      customerNotesFromEnquiry: categorizedNotes.customerNotes.join('\n\n') || '',
      // Initialize empty note fields for new offer notes
      salesNotes: '',
      designNotes: '',
      customerNotes: '',
      audiences: [{
        organization_id: '',
        channel: '',
        adType: '',
        adGroup: 1,
        startDate: getCurrentDateForInput(),
        endDate: getFutureDateForInput(365), // Default 1 year from now
        price: enquiry.invoiceAmount || '',
        discount: '0',
        discountType: '$'
      }]
    };

    setOfferFormData(currentFormData => ({ ...currentFormData, ...defaultOfferData }));
    setCreateOfferDialogOpen(true);
    setIsEditingOffer(false);
    setEditingOfferId(null);
  };

  // Handle generating offer - navigate to offer creation page
  const handleGenerateOfferFromOffer = (offer) => {
    navigate(`/app/offer/create/${offer.id}`);
  };


  // Handle canceling an active offer — resets to New so a new one can be generated
  const handleCancelOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to cancel this offer? The client link will be deactivated and you can generate a new offer.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/offers/${offerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'New', generated_url: null })
      });
      if (response.ok) {
        setSnackbar({ open: true, message: 'Offer cancelled — you can now generate a new one', severity: 'success' });
        await fetchOffers();
      } else {
        throw new Error('Failed to cancel offer');
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error cancelling offer. Please try again.', severity: 'error' });
    }
  };

  // Handle canceling a generated proposal
  const handleCancelProposal = async (offerId) => {
    if (!window.confirm('Are you sure you want to cancel this proposal? This will deactivate the client link and reset the offer status.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/offers/${offerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'Cancelled',
          generated_url: null
        })
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Proposal cancelled successfully',
          severity: 'success'
        });
        // Refresh offers list
        await fetchOffers();
      } else {
        throw new Error('Failed to cancel proposal');
      }
    } catch (error) {
      console.error('Error canceling proposal:', error);
      setSnackbar({
        open: true,
        message: 'Error canceling proposal. Please try again.',
        severity: 'error'
      });
    }
  };

  // Handle viewing change requests for a "Needs Changes" offer
  const handleViewChangeRequests = async (offerId) => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/offers/${offerId}/change-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChangeRequestsDialog({
          open: true,
          offerId,
          requests: data.data || []
        });
      } else {
        setSnackbar({ open: true, message: 'Failed to load change requests', severity: 'error' });
      }
    } catch (error) {
      console.error('Error fetching change requests:', error);
      setSnackbar({ open: true, message: 'Error loading change requests', severity: 'error' });
    }
  };

  // Handle creating order from offer
  const handleCreateOrderFromOffer = async (offer) => {
    
    // Helper function to format date from offer data
    // Uses formatDateForInput (local timezone) to avoid off-by-one UTC issues
    const formatOfferDate = (dateStr) => {
      if (!dateStr) return getCurrentDateForInput();
      try {
        const formatted = formatDateForInput(dateStr);
        return formatted || getCurrentDateForInput();
      } catch {
        return getCurrentDateForInput();
      }
    };

    // Determine Product Type based on offer source
    let productType = 'New'; // Default
    if (offer.offerSource === 'enquiry') {
      productType = 'Lead';
    } else if (offer.offerSource === 'follow_up') {
      productType = 'Repeat';
    } else if (offer.offerSource === 'manual') {
      productType = 'New';
    }

    // Fetch offer attachments to display in the form
    let offerAttachments = [];
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/offer-attachments/${offer.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        offerAttachments = data.data || [];
      }
    } catch (error) {
      console.error('Error fetching offer attachments:', error);
    }

    // Map offer data to order data structure
    const defaultOrderData = {
      organization_id: offer.organizationId || '',
      organization: offer.organizationId || '',  // Add organization field for backend compatibility
      business_number: offer.organizationId?.toString() || '',
      business_name: offer.businessName || '',
      business_type: offer.businessType || 'existing',
      orderType: 'ad',
      productType: productType, // Set based on offer source
      // Carry over notes from offer (keeping them separate for proper display).
      // No "Order created from offer #X" marker here - the offer_id field already
      // tracks the linkage, and the original note content (with its own "(Offer
      // Creation)" label) is what should actually be visible.
      designNotes: '', // Keep design notes separate for the form
      // Store original offer notes for proper display in the form
      salesNotesFromOffer: offer.notes || '',
      designNotesFromOffer: offer.designNotes || '',
      // Additional fields for new notes
      // Additional sales notes for the new order (offer notes carried over separately via salesNotesFromOffer)
      salesNotes: '',
      // Include offer attachments (plus the offer's single embedded "Offer Image",
      // if any) as tempAttachments so they all display in the form and get carried
      // over to the order when it's created.
      tempAttachments: [
        ...(normalizeImageDataUrl(offer.imageData) ? [{
          id: 'offer-image',
          name: 'offer-image.jpg',
          size: null,
          type: 'image/jpeg',
          fromOffer: true,
          fromOfferImage: true, // Flag so submit knows to convert+upload this base64 image
          preview: normalizeImageDataUrl(offer.imageData)
        }] : []),
        ...offerAttachments.map(att => ({
          id: att.id,
          name: att.originalName,
          size: att.fileSize,
          type: att.mimeType,
          uploadedAt: att.uploadedAt,
          uploadedBy: att.uploadedBy,
          uploaderName: att.uploaderName,
          fromOffer: true, // Flag to indicate this came from the offer
          // Attachments already saved on the offer are served statically from
          // /uploads (no auth needed), so an <img> preview can point at them
          // directly - without this, image attachments carried over from an
          // offer showed a generic file icon instead of a thumbnail.
          preview: att.mimeType && att.mimeType.startsWith('image/')
            ? `${API_URL.replace('/api', '')}/uploads/${att.filePath}`
            : null
        }))
      ],
      // Keep the original notes for backward compatibility
      audiences: offer.audiences?.length > 0 ? offer.audiences.map((audience, index) => {
        return {
          organization_id: audience.organizationId || '',
          channel: audience.channel || '',
          adType: audience.adType || '',
          adGroup: audience.adGroup || index + 1,
          startDate: formatOfferDate(audience.startDate),
          endDate: formatOfferDate(audience.endDate),
          price: audience.price?.toString() || '',
          discount: audience.discount?.toString() || '0',
          discountType: audience.discountType || '$',
          eLinkType: 'None',
          eLinkContent: '',
          addons: audience.addons || null,
          manualPriceOverride: audience.manualPriceOverride || false
        };
      }) : [{
        organization_id: '',
        channel: '',
        adType: '',
        adGroup: 1,
        startDate: getCurrentDateForInput(),
        endDate: getFutureDateForInput(365), // Default 1 year from now
        price: '',
        discount: '0',
        discountType: '$',
        eLinkType: 'None',
        eLinkContent: ''
      }]
    };
    setOrderFormData(defaultOrderData);
    setSelectedOfferForOrder(offer);
    setCreateOrderDialogOpen(true);
  };

  // Audience management functions for the order form
  const handleAudienceChange = (index, field, value) => {
    const newAudiences = [...orderFormData.audiences];
    newAudiences[index] = { ...newAudiences[index], [field]: value };
    setOrderFormData({ ...orderFormData, audiences: newAudiences });
  };

  const addAudience = () => {
    const firstAudience = orderFormData.audiences[0] || {};
    const newAudience = {
      organization_id: '',
      channel: '',
      adType: '',
      adGroup: orderFormData.audiences.length + 1,
      startDate: firstAudience.startDate || getCurrentDateForInput(),
      endDate: firstAudience.endDate || getFutureDateForInput(365), // Default 1 year from now
      price: '',
      discount: '0',
      discountType: '$',
      eLinkType: firstAudience.eLinkType || 'None',
      eLinkContent: firstAudience.eLinkType !== 'None' ? (firstAudience.eLinkContent || '') : ''
    };
    
    setOrderFormData({
      ...orderFormData,
      audiences: [...orderFormData.audiences, newAudience]
    });
  };

  const removeAudience = (index) => {
    const newAudiences = orderFormData.audiences.filter((_, i) => i !== index);
    setOrderFormData({ ...orderFormData, audiences: newAudiences });
  };

  // Handle creating business from offer (for offers without existing organization)
  const handleConvertOfferToBusiness = async (offer) => {
    // Set organization form data with offer information
    const organizationData = {
      name: offer.businessName || '',
      business_type: '', // Don't autofill business type
      // Autofill contact information from offer (originally from enquiry)
      primary_name: offer.contactPerson || '',
      primary_email: offer.contactEmail || '',
      primary_phone: offer.contactPhone || '',
      notes: `Business created from offer #${offer.id}`,
      is_business: true,
      is_school: false,
      is_active: true,
      sales_consultant_id: user.id // Default to current user
    };

    setOrganizationFormData(organizationData);
    setEnquiryToConvert({ businessName: offer.businessName, id: offer.id });
    setConvertDialogOpen(true);
    
    // Get and store token for the dialog
    const authToken = await getToken();
    setToken(authToken);
  };

  // Reset organization form data
  const resetOrganizationFormData = () => {
    setOrganizationFormData({
      name: '',
      businessType: '',
      primaryName: '',
      primaryEmail: '',
      primaryPhone: '',
      accountsName: '',
      accountsEmail: '',
      accountsPhone: '',
      generalEmail: '',
      generalPhone: '',
      website: '',
      address: '',
      suburb: '',
      state: '',
      postcode: '',
      notes: '',
      isBusiness: true,
      isSchool: false,
      isActive: true
    });
  };

  // Offer form handlers
  const handleOfferAudienceChange = async (index, field, value) => {
    const newAudiences = [...offerFormData.audiences];
    newAudiences[index] = { ...newAudiences[index], [field]: value };
    
    // Auto-calculate price when relevant fields change
    if (['organization_id', 'channel', 'adType', 'startDate', 'endDate'].includes(field)) {
      const audience = newAudiences[index];
      if (audience.organization_id && audience.channel && audience.adType && audience.startDate && audience.endDate) {
        try {
          const result = await PricingService.calculatePrice(
            100, // base price - will be calculated on server
            audience.channel,
            audience.adType,
            audience.startDate,
            audience.endDate,
            1, // quantity
            audience.organization_id
          );
          if (result.success) {
            newAudiences[index].price = result.price.toString();
          }
        } catch (error) {
          console.error('Error calculating price:', error);
        }
      }
    }
    
    setOfferFormData(currentFormData => ({ ...currentFormData, audiences: newAudiences }));
  };

  // Handle channel or ad type changes with dependent field reset
  const handleOfferChannelOrAdTypeChange = async (index, field, value) => {
    const newAudiences = [...offerFormData.audiences];
    
    if (field === 'channel') {
      // Reset ad type when channel changes
      newAudiences[index] = { 
        ...newAudiences[index], 
        channel: value, 
        adType: '',
        price: '' // Reset price when channel changes
      };
    } else if (field === 'adType') {
      newAudiences[index] = { 
        ...newAudiences[index], 
        adType: value 
      };
    }
    
    // Update form data first
    setOfferFormData(currentFormData => ({ ...currentFormData, audiences: newAudiences }));
    
    // Then trigger price calculation if all required fields are present
    const audience = newAudiences[index];
    if (audience.organization_id && audience.channel && audience.adType && audience.startDate && audience.endDate) {
      try {
        const result = await PricingService.calculatePrice(
          100, // base price - will be calculated on server
          audience.channel,
          audience.adType,
          audience.startDate,
          audience.endDate,
          1, // quantity
          audience.organization_id
        );
        if (result.success) {
          setOfferFormData(currentFormData => {
            const updatedAudiences = [...currentFormData.audiences];
            updatedAudiences[index] = {
              ...updatedAudiences[index],
              price: result.price.toString()
            };
            return { ...currentFormData, audiences: updatedAudiences };
          });
        }
      } catch (error) {
        console.error('Error calculating price:', error);
      }
    }
  };

  const addOfferAudience = () => {
    setOfferFormData(currentFormData => {
    const newAudience = {
      organization_id: '',
      channel: '',
      adType: '',
        adGroup: currentFormData.audiences.length + 1,
      startDate: getCurrentDateForInput(),
      endDate: getFutureDateForInput(365), // Default 1 year from now
      price: '',
      discount: '0',
      discountType: '$'
    };
      return {
        ...currentFormData,
        audiences: [...currentFormData.audiences, newAudience]
      };
    });
  };

  const removeOfferAudience = (index) => {
    const newAudiences = offerFormData.audiences.filter((_, i) => i !== index);
    setOfferFormData(currentFormData => ({ ...currentFormData, audiences: newAudiences }));
  };

  const calculateOfferTotal = () => {
    return offerFormData.audiences.reduce((total, audience) => {
      const price = parseFloat(audience.price) || 0;
      const discount = parseFloat(audience.discount) || 0;
      const discountAmount = audience.discountType === '%' 
        ? (price * discount / 100) 
        : discount;
      return total + (price - discountAmount);
    }, 0);
  };

  // Handle editing an existing offer
  const handleEditOffer = async (offer) => {
    
    try {
      const token = await getToken();
      
      // Fetch full offer data including audiences
      const response = await fetch(`${API_URL}/offers/${offer.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const offerData = await response.json();
        
        // Convert offer data back to form format
        const formattedOfferData = {
          business_name: offerData.data.businessName,
          businessType: offerData.data.businessType,
          organization_id: offerData.data.organizationId,
          // Include notes fields
          salesNotes: offerData.data.notes || '',
          designNotes: offerData.data.designNotes || '',
          customerNotes: offerData.data.customerNotes || '',
          imageCaption: offerData.data.imageCaption || '',
          imageData: offerData.data.imageData || '',
          audiences: offerData.data.audiences.map(audience => ({
            organization_id: audience.organizationId,
            channel: audience.channel,
            adType: audience.adType,
            adGroup: audience.adGroup,
            startDate: audience.startDate ? formatDateForInput(audience.startDate) : '',
            endDate: audience.endDate ? formatDateForInput(audience.endDate) : '',
            price: audience.price,
            discount: audience.discount || 0,
            discountType: audience.discountType || '$',
            addons: audience.addons || null,
            manualPriceOverride: audience.manualPriceOverride || false
          }))
        };
        
        setOfferFormData(formattedOfferData);
        setIsEditingOffer(true);
        setEditingOfferId(offer.id);
        setCreateOfferDialogOpen(true);
      } else {
        throw new Error('Failed to fetch offer details');
      }
    } catch (error) {
      console.error('Error fetching offer for edit:', error);
      setSnackbar({
        open: true,
        message: `Error loading offer: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Handle deleting/canceling an offer
  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/offers/${offerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Offer deleted successfully',
          severity: 'success'
        });
        // Refresh offers list
        fetchOffers();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete offer');
      }
    } catch (error) {
      console.error('Error deleting offer:', error);
      setSnackbar({
        open: true,
        message: `Error deleting offer: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // Handle canceling the create offer dialog
  const handleCancelCreateOffer = () => {
    // Reset form data to initial state
    setOfferFormData({
      businessType: 'existing',
      organization_id: '',
      business_name: '',
      notes: '',
      offerNotes: '',
      designNotes: '',
      imageCaption: '',
      imageData: '',
      audiences: [{
        organization_id: '',
        channel: '',
        adType: '',
        adGroup: 1,
        startDate: '',
        endDate: '',
        price: '',
        discount: '0',
        discountType: '$'
      }]
    });
    // Reset edit mode states
    setIsEditingOffer(false);
    setEditingOfferId(null);
    setCreateOfferDialogOpen(false);
    setShowMapPanel(false);
  };

  // Handle opening order edit dialog
  const handleEditOrder = async (order) => {
    try {
      setLoading(true);
      const token = await getToken();
      
      // Fetch full order details including audiences
      const response = await fetch(`${API_URL}/orders/${order.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }
      
      const responseData = await response.json();
      
      // Extract the actual order data (it's wrapped in a 'data' property)
      const orderDetails = responseData.data || responseData;
      
      // Check if order is Invoiced - show update dialog instead of edit dialog
      if (orderDetails.status === 'Invoiced') {
        setSelectedOrderForUpdate(orderDetails);
        setCreateUpdateDialogOpen(true);
        setLoading(false);
        return;
      }
      
      // Set up form data for editing with proper field mapping
      const formData = {
        ...orderDetails,
        orderType: 'ad',
        status: orderDetails.status || 'New',
        // Ensure all business fields are properly mapped
        organization_id: orderDetails.organization || orderDetails.organization_id,
        organization: orderDetails.organization || orderDetails.organization_id,
        businessName: orderDetails.businessName || orderDetails.business_name || '',
        business_name: orderDetails.businessName || orderDetails.business_name || '',
        businessNumber: orderDetails.businessNumber || orderDetails.business_number || '',
        business_number: orderDetails.businessNumber || orderDetails.business_number || '',
        businessType: orderDetails.businessType || orderDetails.business_type || '',
        business_type: orderDetails.businessType || orderDetails.business_type || '',
        productType: orderDetails.productType || orderDetails.product_type || '',
        totalPrice: orderDetails.totalPrice || orderDetails.total_price || 0,
        notes: orderDetails.notes || '',
        salesNotes: orderDetails.notes || '',
        designNotes: orderDetails.designNotes || orderDetails.design_notes || '',
        customerNotes: orderDetails.customerNotes || orderDetails.customer_notes || '',
        // Ensure audiences array exists and is properly formatted
        audiences: (orderDetails.audiences || []).map(audience => ({
          ...audience,
          organization_id: audience.organization || audience.organization_id,
          organization: audience.organization || audience.organization_id,
          adType: audience.adType || audience.ad_type,
          ad_type: audience.adType || audience.ad_type,
          startDate: audience.startDate || audience.start_date,
          start_date: audience.startDate || audience.start_date,
          endDate: audience.endDate || audience.end_date,
          end_date: audience.endDate || audience.end_date,
          eLinkType: audience.eLinkType || audience.e_link_type || 'None',
          e_link_type: audience.eLinkType || audience.e_link_type || 'None',
          eLinkContent: audience.eLinkContent || audience.e_link_content || '',
          e_link_content: audience.eLinkContent || audience.e_link_content || '',
          price: audience.price ? parseFloat(audience.price) : 0,
          discount: audience.discount ? parseFloat(audience.discount) : 0,
          discountType: audience.discountType || '$',
          subTotal: audience.subTotal || audience.sub_total || 0,
          sub_total: audience.subTotal || audience.sub_total || 0,
          // Parse addons if it's a string
          addons: typeof audience.addons === 'string' ? 
            (audience.addons ? JSON.parse(audience.addons) : {}) : 
            (audience.addons || {})
        }))
      };
      
      setEditOrderFormData(formData);
      // Set the editing order with the organization_id for locking
      setEditingOrder({
        ...order,
        organization_id: formData.organization_id || formData.organization
      });
      setEditOrderDialogOpen(true);
      
    } catch (error) {
      console.error('Error fetching order details:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load order details for editing',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle order edit form changes
  const handleEditOrderAudienceChange = async (index, field, value) => {
    const newAudiences = [...editOrderFormData.audiences];
    newAudiences[index] = { ...newAudiences[index], [field]: value };
    
    // Auto-calculate price when relevant fields change
    if (['organization_id', 'channel', 'adType', 'startDate', 'endDate'].includes(field)) {
      const audience = newAudiences[index];
      if (audience.organization_id && audience.channel && audience.adType && audience.startDate && audience.endDate) {
        try {
          const result = await PricingService.calculatePrice(
            100, // base price - will be calculated on server
            audience.channel,
            audience.adType,
            audience.startDate,
            audience.endDate,
            1, // quantity
            audience.organization_id
          );
          if (result.success) {
            newAudiences[index].price = result.price.toString();
          }
        } catch (error) {
          console.error('Error calculating price:', error);
        }
      }
    }
    
    setEditOrderFormData(currentFormData => ({ ...currentFormData, audiences: newAudiences }));
  };

  // Handle saving edited order
  const handleSaveEditedOrder = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      // Get the current order status
      const currentStatus = editingOrder.status;
      
      // Detect if there are any changes
      const hasChanges = JSON.stringify(editingOrder) !== JSON.stringify(editOrderFormData);
      
      if (!hasChanges) {
        setSnackbar({
          open: true,
          message: 'No changes detected',
          severity: 'info'
        });
        setEditOrderDialogOpen(false);
        setLoading(false);
        return;
      }
      
      // Statuses that allow direct updates without creating an update order
      const directUpdateStatuses = ['New', 'Waiting for Artwork', 'In Design', 'Proofing'];
      
      // Statuses that require creating an update order
      const updateOrderStatuses = ['Approved', 'Uploaded', 'Invoiced'];
      
      if (directUpdateStatuses.includes(currentStatus)) {
        // Direct update - just update the order
        const response = await fetch(`${API_URL}/orders/${editingOrder.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editOrderFormData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to update order');
        }

        // Optimistically stamp updatedAt so the timestamp shows immediately
        const nowIso = new Date().toISOString();
        setOrders(prev => prev.map(o =>
          o.id === editingOrder.id ? { ...o, status: editOrderFormData.status || o.status, updatedAt: nowIso } : o
        ));
        
        // Refresh orders data
        await loadSalesData();
        
        setEditOrderDialogOpen(false);
        setEditingOrder(null);
        setEditOrderFormData({});
        
        setSnackbar({
          open: true,
          message: 'Order updated successfully (Update to Order in Progress)',
          severity: 'success'
        });
        
      } else if (updateOrderStatuses.includes(currentStatus)) {
        // Create an update order to track the changes
        
        // First, update the original order
        const updateResponse = await fetch(`${API_URL}/orders/${editingOrder.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editOrderFormData)
        });
        
        if (!updateResponse.ok) {
          throw new Error('Failed to update order');
        }
        
        // Calculate what changed
        const changes = [];
        
        // Compare audiences
        if (JSON.stringify(editingOrder.audiences) !== JSON.stringify(editOrderFormData.audiences)) {
          changes.push('Audiences modified');
        }
        
        // Compare other key fields
        const fieldsToCheck = ['businessName', 'totalPrice', 'status', 'notes', 'designNotes'];
        fieldsToCheck.forEach(field => {
          if (editingOrder[field] !== editOrderFormData[field]) {
            changes.push(`${field} changed from "${editingOrder[field]}" to "${editOrderFormData[field]}"`);
          }
        });
        
        // Create update order with details of changes
        const updateOrderData = {
          organizationId: editingOrder.organization_id || editingOrder.organization || editOrderFormData.organization_id || editOrderFormData.organization,
          updateType: 'Order Update',
          relatedOrderId: editingOrder.id,
          relatedOrderBusinessName: editingOrder.businessName || editOrderFormData.businessName,
          orderUpdateType: 'content',
          notes: `Order edited from Sales Workflow.\n\nChanges made:\n${changes.join('\n')}`,
          status: 'New',
          salesPersonId: user.id
        };
        
        const createUpdateResponse = await fetch(`${API_URL}/update-orders`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateOrderData)
        });
        
        if (!createUpdateResponse.ok) {
          console.error('Failed to create update order, but original order was updated');
        }

        // Optimistically stamp updatedAt so the timestamp shows immediately
        const nowIso = new Date().toISOString();
        setOrders(prev => prev.map(o =>
          o.id === editingOrder.id ? { ...o, status: editOrderFormData.status || o.status, updatedAt: nowIso } : o
        ));
        
        // Refresh orders data
        await loadSalesData();
        
        setEditOrderDialogOpen(false);
        setEditingOrder(null);
        setEditOrderFormData({});
        
        setSnackbar({
          open: true,
          message: 'Order updated successfully and update order created',
          severity: 'success'
        });
      } else {
        // Unknown status - default to direct update
        const response = await fetch(`${API_URL}/orders/${editingOrder.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editOrderFormData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to update order');
        }

        // Optimistically stamp updatedAt so the timestamp shows immediately
        const nowIso = new Date().toISOString();
        setOrders(prev => prev.map(o =>
          o.id === editingOrder.id ? { ...o, status: editOrderFormData.status || o.status, updatedAt: nowIso } : o
        ));
        
        await loadSalesData();
        
        setEditOrderDialogOpen(false);
        setEditingOrder(null);
        setEditOrderFormData({});
        
        setSnackbar({
          open: true,
          message: 'Order updated successfully',
          severity: 'success'
        });
      }
      
    } catch (error) {
      console.error('Error updating order:', error);
      setSnackbar({
        open: true,
        message: `Failed to update order: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = async () => {
    // Guard against double-clicking Save/Update, which was creating duplicate offers.
    if (isSubmittingOffer) return;
    setIsSubmittingOffer(true);
    try {
      const token = await getToken();
      
      // Prepare the offer data for the API
      // When editing, use existing notes as-is; when creating, wrap new notes with metadata
      const offerData = {
        organization_id: offerFormData.businessType === 'existing' ? offerFormData.organization_id : null,
        business_name: offerFormData.business_name,
        business_type: offerFormData.businessType,
        offer_source: offerFormData.offer_source || 'manual', // Default to manual if not specified
        expiry_date: getFutureDateForInput(7), // Default to 7 days from now
        contact_person: offerFormData.contact_person || user?.name || user?.email || 'Unknown',
        contact_email: offerFormData.contact_email || null,
        contact_phone: offerFormData.contact_phone || null,
        notes: isEditingOffer 
          ? (offerFormData.salesNotes || null)  // When editing, use notes as-is
          : [
              offerFormData.salesNotesFromEnquiry, // Sales notes from enquiry
              offerFormData.salesNotes || null // Notes are already formatted by OfferForm
            ].filter(Boolean).join('\n\n') || null,
        designNotes: isEditingOffer
          ? (offerFormData.designNotes || null)  // When editing, use notes as-is
          : [
              offerFormData.designNotesFromEnquiry, // Design notes from enquiry
              offerFormData.designNotes || null // Notes are already formatted by OfferForm
            ].filter(Boolean).join('\n\n') || null,
        customerNotes: isEditingOffer
          ? (offerFormData.customerNotes || null)  // When editing, use notes as-is
          : [
              offerFormData.customerNotesFromEnquiry, // Customer notes from enquiry
              offerFormData.customerNotes || null // Notes are already formatted by OfferForm
            ].filter(Boolean).join('\n\n') || null,
        imageCaption: offerFormData.imageCaption || null,
        imageData: offerFormData.imageData || null,
        audiences: offerFormData.audiences.map(audience => ({
          organization_id: audience.organization_id,
          channel: audience.channel,
          adType: audience.adType,
          adGroup: audience.adGroup,
          startDate: audience.startDate,
          endDate: audience.endDate,
          price: audience.price,
          discount: audience.discount || 0,
          discountType: audience.discountType || '$',
          addons: audience.addons || null,
          manualPriceOverride: audience.manualPriceOverride || false
        }))
      };

      // Determine URL and method based on edit mode
      const url = isEditingOffer ? `${API_URL}/offers/${editingOfferId}` : `${API_URL}/offers`;
      const method = isEditingOffer ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(offerData)
      });

      if (response.ok) {
        const result = await response.json();
        const offerId = result.data?.id;
        
        // Upload attachments if any exist and we have an offer ID
        if (offerFormData.tempAttachments && offerFormData.tempAttachments.length > 0 && offerId) {
          try {
            setSnackbar({
              open: true,
              message: `Offer ${isEditingOffer ? 'updated' : 'created'}! Uploading ${offerFormData.tempAttachments.length} attachments...`,
              severity: 'info'
            });

            await uploadTempAttachments(offerId, offerFormData.tempAttachments, getToken, null, 'offer');
            
            setSnackbar({
              open: true,
              message: `Offer ${isEditingOffer ? 'updated' : 'created'} successfully with ${offerFormData.tempAttachments.length} attachments!`,
              severity: 'success'
            });
          } catch (attachmentError) {
            console.error('Error uploading attachments:', attachmentError);
            setSnackbar({
              open: true,
              message: `Offer ${isEditingOffer ? 'updated' : 'created'} but failed to upload attachments: ${attachmentError.message}`,
              severity: 'warning'
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: isEditingOffer ? 'Offer updated successfully!' : 'Offer created successfully!',
            severity: 'success'
          });
        }

        // Link the offer back to the outreach contact (if created via the outreach flow)
        if (!isEditingOffer && offerId && pendingOutreachContactId) {
          try {
            const linkRes = await fetch(`${API_URL}/outreach/contacts/${pendingOutreachContactId}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ offer_id: offerId, status: 'offer_created' }),
            });
            if (!linkRes.ok) {
              console.error('Failed to link outreach contact to offer:', await linkRes.text());
            }
          } catch (linkErr) {
            console.error('Error linking outreach contact to offer:', linkErr);
          }
          setPendingOutreachContactId(null);
        }

        // Update the enquiry outcome if this offer was created from an enquiry
        if (!isEditingOffer && offerFormData.enquiry_id) {
          try {
            // Update the enquiry outcome to "Offered"
            await fetch(`${API_URL}/enquiries/${offerFormData.enquiry_id}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                outcome: 'Offered'
              })
            });
          } catch (updateError) {
            console.error('Error updating enquiry outcome:', updateError);
            // Don't show error to user - this is a background operation
          }
        }
        
        // Reset form and close dialog
        setOfferFormData({
          businessType: 'existing',
          organization_id: '',
          business_name: '',
          audiences: [{
            organization_id: '',
            channel: '',
            adType: '',
            adGroup: 1,
            startDate: '',
            endDate: '',
            price: '',
            discount: '0',
            discountType: '$'
          }]
        });
        // Reset edit mode states
        setIsEditingOffer(false);
        setEditingOfferId(null);
        setCreateOfferDialogOpen(false);
        
        // Reload offers data
        loadSalesData();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isEditingOffer ? 'update' : 'create'} offer`);
      }
    } catch (error) {
      console.error(`Error ${isEditingOffer ? 'updating' : 'creating'} offer:`, error);
      setSnackbar({
        open: true,
        message: `Error ${isEditingOffer ? 'updating' : 'creating'} offer: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  // Function to get available channels for an organization
  const getAvailableChannels = (organizationId) => {
    if (!organizationId) return [];
    
    const selectedOrg = organizations.find(org => org.id === organizationId);
    if (!selectedOrg) return [];
    
    const channels = [];
    
    // Add channels based on organization properties
    if (selectedOrg.hasSchoolLink === true || selectedOrg.hasSchoolLink === 1) {
      channels.push("SchoolLink");
    }
    
    if (selectedOrg.hasEPublisher === true || selectedOrg.hasEPublisher === 1) {
      channels.push("ePublisher");
    }
    
    if (selectedOrg.hasPaper === true || selectedOrg.hasPaper === 1) {
      channels.push("Paper");
    }
    
    // If this is a school and no channels are available, provide default channels
    if (channels.length === 0 && (selectedOrg.isSchool === true || selectedOrg.isSchool === 1)) {
      // Default channels for schools - can be customized based on business requirements
      channels.push("SchoolLink", "ePublisher", "Paper");
    }
    
    return channels;
  };

  // Function to get available ad types based on selected channel
  const getAvailableAdTypes = (channel) => {
    if (!channel) return [];
    
    switch (channel) {
      case 'Paper':
        return ['Standard', 'Budget', 'Premium', 'POW'];
      case 'ePublisher':
        return ['Standard', 'Budget', 'Premium', 'POW'];
      case 'SchoolLink':
        return ['Single Image', 'Carousel', 'Video or Animation', 'Standard', 'Premium'];
      default:
        return [];
    }
  };

  const handleCancelConvert = () => {
    setConvertDialogOpen(false);
    setEnquiryToConvert(null);
    resetOrganizationFormData();
  };



  // Load organizations when component mounts
  useEffect(() => {
    loadOrganizations();
  }, []);

  const getStatusColor = (status) => {
    const statusColors = {
      'New': 'primary',
      'Contacted': 'info',
      'Qualified': 'success',
      'Proposal': 'warning',
      'Won': 'success',
      'Lost': 'error',
      'In Design': 'info',
      'Proofing': 'warning',
      'Approved': 'success',
      'Cancelled': 'error'
    };
    return statusColors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const priorityColors = {
      'High': 'error',
      'Medium': 'warning',
      'Low': 'success'
    };
    return priorityColors[priority] || 'default';
  };

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Sales Workflow
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your sales pipeline from enquiries to renewals
            </Typography>
          </Box>
        </Box>


        {/* Main Content Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="sales workflow tabs">
              <Tab label="Enquiries" icon={<TrendingUpIcon />} />
              <Tab label="Offers" icon={<AssignmentIcon />} />
              <Tab label="Order Tracking" icon={<AssignmentIcon />} />
              <Tab label="Follow Ups" icon={<ScheduleIcon />} />
            </Tabs>
          </Box>

          {/* Enquiries Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ p: 3 }}>
              {/* Search and Filter */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  placeholder="Search enquiries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Outcome</InputLabel>
                  <Select
                    value={outcomeFilter}
                    onChange={(e) => setOutcomeFilter(e.target.value)}
                    label="Outcome"
                  >
                    <MenuItem value="all">All Outcomes</MenuItem>
                    {outcomes.map(outcome => (
                      <MenuItem key={outcome} value={outcome}>
                        {outcome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Enquiries Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Business Name</TableCell>
                      <TableCell>Contact Person</TableCell>
                      <TableCell>Contact Info</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Lead Source</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enquiries
                      .filter(enquiry => {
                        // Filter by search term
                        const matchesSearch = searchTerm === '' || 
                          enquiry.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          enquiry.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          enquiry.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          enquiry.phone?.includes(searchTerm);
                        
                        // Filter by outcome
                        const matchesOutcome = outcomeFilter === 'all' || enquiry.outcome === outcomeFilter;
                        
                        // Exclude 'Offered' and 'Not Interested' enquiries from the sales workflow
                        const isActiveEnquiry = enquiry.outcome !== 'Offered' && enquiry.outcome !== 'Not Interested';
                        
                        return matchesSearch && matchesOutcome && isActiveEnquiry;
                      })
                      .map((enquiry) => (
                      <TableRow key={enquiry.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {enquiry.businessName || 'Unknown Business'}
                          </Typography>
                        </TableCell>
                        <TableCell>{enquiry.contactPerson || 'Unknown Contact'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="caption">{enquiry.email || 'No email'}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="caption">{enquiry.phone || 'No phone'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
                            <Chip 
                              label={enquiry.outcome || 'New'} 
                              color={getOutcomeColor(enquiry.outcome || 'New')} 
                              size="small" 
                            />
                            {enquiry.outcome === 'Follow Up Later' && enquiry.followUpDate && (
                              <Typography variant="caption" color="text.secondary">
                                Due: {formatDate(enquiry.followUpDate)}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{enquiry.leadSource || 'Unknown'}</TableCell>
                        <TableCell>{formatDate(enquiry.date)}</TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                              const existingNotes = [];
                              if (enquiry.salesNotes) {
                                existingNotes.push({
                                  id: 'sales',
                                  content: enquiry.salesNotes,
                                  createdBy: 'Sales Team',
                                  createdAt: enquiry.createdAt || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'sales'
                                });
                              }
                              if (enquiry.customerNotes) {
                                existingNotes.push({
                                  id: 'customer',
                                  content: enquiry.customerNotes,
                                  createdBy: 'Customer Service',
                                  createdAt: enquiry.createdAt || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'customer'
                                });
                              }
                              if (enquiry.followUpNotes) {
                                existingNotes.push({
                                  id: 'followup',
                                  content: enquiry.followUpNotes,
                                  createdBy: 'Sales Team',
                                  createdAt: enquiry.followUpDate || enquiry.createdAt || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'sales' // Follow-up notes are sales-related
                                });
                              }
                              handleOpenNotesDialog('enquiry', enquiry.id.toString(), enquiry.businessName, existingNotes);
                            }}
                            sx={{ 
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              borderRadius: 1,
                              color: (() => {
                                const totalCount = getTotalNoteCount(enquiry, 'enquiry');
                                return totalCount > 0 ? 'primary.main' : 'text.secondary';
                              })(),
                              '&:hover': {
                                bgcolor: 'action.hover',
                              }
                            }}
                          >
                            Notes
                            {(() => {
                              const totalCount = getTotalNoteCount(enquiry, 'enquiry');
                              return totalCount > 0 ? (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.5,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    px: 0.5,
                                    py: 0.1,
                                    borderRadius: '50%',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    minWidth: 18,
                                    height: 18,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {totalCount}
                                </Box>
                              ) : null;
                            })()}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Stack direction="column" spacing={1} alignItems="flex-start">
                            {/* Outcome Action Buttons */}
                            <EnquiryActions 
                              enquiry={enquiry}
                              onOutcomeChange={handleEnquiryOutcomeChange}
                            />
                            
                            {/* Business Management Actions */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button 
                                variant="text"
                                size="small" 
                                color="success"
                                onClick={() => handleCreateOfferFromEnquiry(enquiry)}
                                sx={{ 
                                  minWidth: 'auto', 
                                  px: 1, 
                                  py: 0.25, 
                                  fontSize: '0.75rem',
                                  textTransform: 'none'
                                }}
                              >
                                Create Offer
                              </Button>
                            </Box>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Offers Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />} 
                  onClick={() => {
                    // Reset edit mode states and set offer source when creating new offer manually
                    setIsEditingOffer(false);
                    setEditingOfferId(null);
                    setOfferFormData(prev => ({ ...prev, offer_source: 'manual' }));
                    setCreateOfferDialogOpen(true);
                  }}
                >
                  Create Offer
                </Button>
              </Box>

              {/* Search and Filter */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  placeholder="Search offers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1 }}
                />
              </Box>

              {/* Offers Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Business Name</TableCell>
                      <TableCell>Creation Date</TableCell>
                      <TableCell>Audiences</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offers
                      .filter(offer => {
                        // Filter by search term
                        const matchesSearch = searchTerm === '' || 
                          offer.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          offer.organizationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (offer.audiences && offer.audiences.some(audience => 
                            audience.organizationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            audience.channel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            audience.adType?.toLowerCase().includes(searchTerm.toLowerCase())
                          ));
                        
                        return matchesSearch;
                      })
                      .map((offer) => (
                      <TableRow key={offer.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {offer.businessName || 'Unknown Business'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(offer.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip 
                            title={offer.audiences?.length > 0 ? 
                              offer.audiences.map(a => `${a.organizationName || 'Unknown'} (${a.channel || 'Unknown'} - ${a.adType || 'Unknown'})`).join('\n') : 
                              'No audiences'
                            }
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, cursor: 'help' }}>
                              {formatAudiencesList(offer.audiences)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {offer.price}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={offer.status} 
                            color={getOfferStatusColor(offer.status)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                              const existingNotes = [];
                              if (offer.notes) {
                                existingNotes.push({
                                  id: 'sales',
                                  content: offer.notes,
                                  createdBy: 'Sales Team',
                                  createdAt: offer.createdAt || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'sales'
                                });
                              }
                              if (offer.designNotes) {
                                existingNotes.push({
                                  id: 'design',
                                  content: offer.designNotes,
                                  createdBy: 'Design Team',
                                  createdAt: offer.createdAt || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'design'
                                });
                              }
                              if (offer.customerNotes) {
                                existingNotes.push({
                                  id: 'customer',
                                  content: offer.customerNotes,
                                  createdBy: 'Customer Team',
                                  createdAt: offer.createdAt || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'customer'
                                });
                              }
                              handleOpenNotesDialog('offer', offer.id.toString(), offer.businessName, existingNotes);
                            }}
                            sx={{ 
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              borderRadius: 1,
                              color: (() => {
                                const hasNotes = offer.notes || offer.designNotes || offer.customerNotes;
                                return hasNotes ? 'secondary.main' : 'text.secondary';
                              })(),
                              '&:hover': {
                                bgcolor: 'action.hover',
                              }
                            }}
                          >
                            Notes
                            {(() => {
                              // Count all note types
                              let totalNotes = 0;
                              if (offer.notes) totalNotes++;
                              if (offer.designNotes) totalNotes++;
                              if (offer.customerNotes) totalNotes++;
                              
                              return totalNotes > 0 ? (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.5,
                                    bgcolor: 'secondary.main',
                                    color: 'secondary.contrastText',
                                    px: 0.5,
                                    py: 0.1,
                                    borderRadius: '50%',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    minWidth: 18,
                                    height: 18,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {totalNotes}
                                </Box>
                              ) : null;
                            })()}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Stack direction="column" spacing={1} alignItems="flex-start">

                            
                            {/* Client Response Display - Only show for change requests */}
                            {offer.status === 'Needs Changes' && (
                              <Box sx={{ mb: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1, width: '100%' }}>
                                <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>
                                  ✏️ Client Requested Changes
                                </Typography>
                                {offer.changesRequestedAt && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {formatDate(offer.changesRequestedAt)}
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  sx={{ mt: 0.5, fontSize: '0.7rem', py: 0.25, textTransform: 'none' }}
                                  onClick={() => handleViewChangeRequests(offer.id)}
                                >
                                  View Change Notes
                                </Button>
                              </Box>
                            )}
                            
                            {/* Generated Offer Controls */}
                            {offer.status === 'Generated' && offer.generatedUrl && (
                              <Box sx={{ mb: 1 }}>
                                <ButtonGroup size="small" variant="outlined">
                                  <Button
                                    color="primary"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={() => window.open(offer.generatedUrl, '_blank')}
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      textTransform: 'none',
                                      py: 0.25
                                    }}
                                  >
                                    View Proposal
                                  </Button>
                                  <Button
                                    color="error"
                                    onClick={() => handleCancelProposal(offer.id)}
                                    sx={{ 
                                      fontSize: '0.75rem',
                                      textTransform: 'none',
                                      py: 0.25
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </ButtonGroup>
                              </Box>
                            )}
                            
                            {/* Offer Action Buttons */}
                            <ButtonGroup size="small" variant="text">
                              <Button 
                                color="info"
                                size="small"
                                onClick={() => handleEditOffer(offer)}
                                sx={{ 
                                  minWidth: 'auto', 
                                  px: 1, 
                                  py: 0.25, 
                                  fontSize: '0.75rem',
                                  textTransform: 'none'
                                }}
                              >
                                Edit
                              </Button>
                              {offer.status !== 'Offered' && offer.status !== 'Accepted' && (
                                <Button 
                                  color="primary"
                                  size="small"
                                  onClick={() => handleGenerateOfferFromOffer(offer)}
                                  sx={{ 
                                    minWidth: 'auto', 
                                    px: 1, 
                                    py: 0.25, 
                                    fontSize: '0.75rem',
                                    textTransform: 'none'
                                  }}
                                >
                                  Generate Offer
                                </Button>
                              )}
                              {offer.status === 'Offered' && (
                                <Button 
                                  color="error"
                                  size="small"
                                  onClick={() => handleCancelOffer(offer.id)}
                                  sx={{ 
                                    minWidth: 'auto', 
                                    px: 1, 
                                    py: 0.25, 
                                    fontSize: '0.75rem',
                                    textTransform: 'none'
                                  }}
                                >
                                  Cancel Offer
                                </Button>
                              )}
                              {/* Show View Offer button if offer has been generated */}
                              {offer.generatedUrl && (
                                <Button 
                                  color="secondary"
                                  size="small"
                                  onClick={() => window.open(offer.generatedUrl, '_blank')}
                                  sx={{ 
                                    minWidth: 'auto', 
                                    px: 1, 
                                    py: 0.25, 
                                    fontSize: '0.75rem',
                                    textTransform: 'none'
                                  }}
                                >
                                  View Offer
                                </Button>
                              )}
                              {/* Show acceptance certificate when offer has been accepted */}
                              {offer.certificateUrl && (
                                <Button
                                  size="small"
                                  onClick={() => window.open(offer.certificateUrl, '_blank')}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.25,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    color: '#009C9E',
                                    borderColor: '#009C9E',
                                    '&:hover': { borderColor: '#007f81', backgroundColor: 'rgba(0,156,158,0.06)' }
                                  }}
                                  variant="outlined"
                                >
                                  📄 Acceptance Certificate
                                </Button>
                              )}
                              {/* Show Create Order if business exists, otherwise Create Business */}
                              {offer.organizationId ? (
                                <Button 
                                  color="success"
                                  size="small"
                                  onClick={() => handleCreateOrderFromOffer(offer)}
                                  sx={{ 
                                    minWidth: 'auto', 
                                    px: 1, 
                                    py: 0.25, 
                                    fontSize: '0.75rem',
                                    textTransform: 'none'
                                  }}
                                >
                                  Create Order
                                </Button>
                              ) : (
                                <Button 
                                  color="warning"
                                  size="small"
                                  onClick={() => handleConvertOfferToBusiness(offer)}
                                  sx={{ 
                                    minWidth: 'auto', 
                                    px: 1, 
                                    py: 0.25, 
                                    fontSize: '0.75rem',
                                    textTransform: 'none'
                                  }}
                                >
                                  Create Business
                                </Button>
                              )}
                              <Button 
                                color="error"
                                size="small"
                                onClick={() => handleDeleteOffer(offer.id)}
                                sx={{ 
                                  minWidth: 'auto', 
                                  px: 1, 
                                  py: 0.25, 
                                  fontSize: '0.75rem',
                                  textTransform: 'none'
                                }}
                              >
                                Cancel
                              </Button>
                            </ButtonGroup>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Order Tracking Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 3 }}>
              {/* Minimalistic Filters */}
              <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Top Row: Search and Status Filter */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search orders..."
                    value={orderSearchTerm}
                    onChange={(e) => setOrderSearchTerm(e.target.value)}
                    sx={{ minWidth: 250, flexGrow: 1, maxWidth: 400 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={orderStatusFilter}
                      onChange={(e) => setOrderStatusFilter(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="past">Past</MenuItem>
                      <MenuItem value="all">All Orders</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Date Filter with Navigation and Calendar Picker */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', position: 'relative' }}>
                    {/* Navigation with arrows */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5
                    }}>
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          if (orderDateFilterType === 'year') {
                            setSelectedOrderYear((parseInt(selectedOrderYear) - 1).toString());
                          } else {
                            navigateMonth('prev');
                          }
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                      
                      <Typography 
                        variant="body2" 
                        onClick={handleOpenCalendar}
                        sx={{ 
                          minWidth: 140, 
                          textAlign: 'center',
                          fontWeight: 500,
                          px: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderRadius: 1
                          }
                        }}
                      >
                        {orderDateFilterType === 'year' 
                          ? selectedOrderYear 
                          : formatMonthDisplay(selectedOrderMonth)
                        }
                      </Typography>
                      
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          if (orderDateFilterType === 'year') {
                            setSelectedOrderYear((parseInt(selectedOrderYear) + 1).toString());
                          } else {
                            navigateMonth('next');
                          }
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Calendar Picker Dropdown */}
                    {calendarOpen && (
                      <>
                        {/* Backdrop to detect outside clicks */}
                        <Box
                          onClick={handleCancelCalendar}
                          sx={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1200
                          }}
                        />
                        
                        {/* Calendar Popup */}
                        <Paper
                          elevation={8}
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            mt: 1,
                            zIndex: 1300,
                            p: 2,
                            minWidth: 280
                          }}
                        >
                          {/* Year selector with arrows */}
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mb: 2
                          }}>
                            <IconButton 
                              size="small" 
                              onClick={() => navigateCalendarYear('prev')}
                            >
                              <ChevronLeftIcon />
                            </IconButton>
                            
                            <Typography 
                              variant="h6"
                              onClick={handleSelectYear}
                              sx={{ 
                                fontWeight: 600,
                                cursor: 'pointer',
                                px: 2,
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                  borderRadius: 1
                                }
                              }}
                            >
                              {calendarYear}
                            </Typography>
                            
                            <IconButton 
                              size="small" 
                              onClick={() => navigateCalendarYear('next')}
                            >
                              <ChevronRightIcon />
                            </IconButton>
                          </Box>

                          {/* Month grid */}
                          <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(3, 1fr)', 
                            gap: 1
                          }}>
                            {monthNames.map((month, index) => (
                              <Button
                                key={month}
                                variant="outlined"
                                size="small"
                                onClick={() => handleSelectMonth(index)}
                                sx={{
                                  py: 1.5,
                                  fontSize: '0.875rem'
                                }}
                              >
                                {month.substring(0, 3)}
                              </Button>
                            ))}
                          </Box>
                        </Paper>
                      </>
                    )}
                  </Box>
                </Box>

                {/* Bottom Row: Filter Summary */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip 
                    label={`${orders.filter(order => {
                      // Include both ad and update orders
                      const isAdOrUpdateOrder = order.orderType === 'ad' || order.order_type === 'ad' || order.orderType === 'update';
                      if (!isAdOrUpdateOrder) return false;
                      
                      const matchesSearch = orderSearchTerm === '' || 
                        order.businessName?.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
                        order.id?.toString().includes(orderSearchTerm) ||
                        order.salesPerson?.toLowerCase().includes(orderSearchTerm.toLowerCase());
                      
                      if (!matchesSearch) return false;
                      
                      // Status filter
                      const now = new Date();
                      const orderStatus = order.status?.toLowerCase() || '';
                      const startDate = order.startDate || order.start_date ? new Date(order.startDate || order.start_date) : null;
                      const endDate = order.endDate || order.end_date ? new Date(order.endDate || order.end_date) : null;
                      
                      if (orderStatusFilter === 'in_progress') {
                        if (orderStatus === 'cancelled' || orderStatus === 'ordered' || orderStatus === 'invoiced' || orderStatus === 'paid' || orderStatus === 'uploaded') return false;
                      } else if (orderStatusFilter === 'active') {
                        if (orderStatus !== 'uploaded' && orderStatus !== 'invoiced' && orderStatus !== 'paid') return false;
                        
                        if (startDate && endDate) {
                          const isCurrent = now >= startDate && now <= endDate;
                          if (!isCurrent) return false;
                        } else {
                          return false;
                        }
                      } else if (orderStatusFilter === 'past') {
                        if (orderStatus !== 'uploaded' && orderStatus !== 'invoiced' && orderStatus !== 'paid') return false;
                        
                        if (endDate) {
                          const isPast = now > endDate;
                          if (!isPast) return false;
                        } else {
                          return false;
                        }
                      }
                      
                      // Date filter (month or year)
                      // In-progress (pre-invoiced) orders float to the current month.
                      // Invoiced/paid/uploaded orders are anchored to their creation month.
                      const isSettled = orderStatus === 'invoiced' || orderStatus === 'paid' || orderStatus === 'uploaded';
                      const dateForFilter = isSettled && order.dateCreated
                        ? new Date(order.dateCreated)
                        : new Date(); // in-progress → always matches today's month
                      
                      if (orderDateFilterType === 'month') {
                        const [selectedYear, selectedMonth] = selectedOrderMonth.split('-').map(Number);
                        const filterYear = dateForFilter.getFullYear();
                        const filterMonth = dateForFilter.getMonth() + 1;
                        if (filterYear !== selectedYear || filterMonth !== selectedMonth) return false;
                      } else {
                        // Year filter
                        const filterYear = dateForFilter.getFullYear();
                        if (filterYear !== parseInt(selectedOrderYear)) return false;
                      }
                      
                      return true;
                    }).length} orders`}
                    size="small" 
                    color="primary"
                  />
                  {orderSearchTerm && (
                    <Chip 
                      label={`"${orderSearchTerm}"`}
                      onDelete={() => setOrderSearchTerm('')}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              {/* Orders Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {[
                        { field: 'id',         label: 'Order ID'       },
                        { field: 'orderType',   label: 'Type'           },
                        { field: 'businessName',label: 'Business Name'  },
                        { field: 'status',      label: 'Status'         },
                        { field: 'value',       label: 'Value'          },
                        { field: 'salesPerson', label: 'Sales Consultant'},
                        { field: 'startDate',   label: 'Start Date'     },
                        { field: 'endDate',     label: 'End Date'       },
                      ].map(({ field, label }) => (
                        <TableCell key={field} sortDirection={orderSortField === field ? orderSortDir : false}>
                          <TableSortLabel
                            active={orderSortField === field}
                            direction={orderSortField === field ? orderSortDir : 'asc'}
                            onClick={() => handleOrderSort(field)}
                          >
                            {label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell>Notes</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders
                      .filter(order => {
                        // First filter: Show advertising orders and update orders
                        const isAdOrUpdateOrder = order.orderType === 'ad' || order.order_type === 'ad' || order.orderType === 'update';
                        if (!isAdOrUpdateOrder) {
                          return false;
                        }
                        
                        // Second filter: Search term
                        const matchesSearch = orderSearchTerm === '' || 
                          order.businessName?.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
                          order.id?.toString().includes(orderSearchTerm) ||
                          order.salesPerson?.toLowerCase().includes(orderSearchTerm.toLowerCase());
                        
                        if (!matchesSearch) {
                          return false;
                        }
                        
                        // Third filter: Status filter (In Progress, Active, Past, All)
                        const now = new Date();
                        const orderStatus = order.status?.toLowerCase() || '';
                        const startDate = order.startDate || order.start_date ? new Date(order.startDate || order.start_date) : null;
                        const endDate = order.endDate || order.end_date ? new Date(order.endDate || order.end_date) : null;
                        
                        if (orderStatusFilter === 'in_progress') {
                          // In Progress: Not Cancelled, Not Ordered, Not Invoiced, Not Paid, Not Uploaded
                          if (orderStatus === 'cancelled' || orderStatus === 'ordered' || orderStatus === 'invoiced' || orderStatus === 'paid' || orderStatus === 'uploaded') {
                            return false;
                          }
                        } else if (orderStatusFilter === 'active') {
                          // Active: Show orders with status "Uploaded", "Invoiced", or "Paid" that are current (within start and end dates)
                          if (orderStatus !== 'uploaded' && orderStatus !== 'invoiced' && orderStatus !== 'paid') {
                            return false;
                          }
                          
                          // Check if order is current (within date range)
                          if (startDate && endDate) {
                            const isCurrent = now >= startDate && now <= endDate;
                            if (!isCurrent) {
                              return false;
                            }
                          } else {
                            // If no dates, exclude from active
                            return false;
                          }
                        } else if (orderStatusFilter === 'past') {
                          // Past: Show orders with status "Uploaded", "Invoiced", or "Paid" where end date has passed
                          if (orderStatus !== 'uploaded' && orderStatus !== 'invoiced' && orderStatus !== 'paid') {
                            return false;
                          }
                          
                          if (endDate) {
                            const isPast = now > endDate;
                            if (!isPast) {
                              return false;
                            }
                          } else {
                            // If no end date, can't be considered "past"
                            return false;
                          }
                        }
                        // If 'all', no status filtering needed
                        
                        // Fourth filter: Date filter (month or year)
                        // In-progress (pre-invoiced) orders float to the current month so they
                        // roll forward automatically. Invoiced/paid/uploaded orders are anchored
                        // to their creation month — they stick once settled.
                        const isSettledOrder = orderStatus === 'invoiced' || orderStatus === 'paid' || orderStatus === 'uploaded';
                        const dateForFilter = isSettledOrder && order.dateCreated
                          ? new Date(order.dateCreated)
                          : new Date(); // in-progress → always matches today's month

                        if (orderDateFilterType === 'month') {
                          const [selectedYear, selectedMonth] = selectedOrderMonth.split('-').map(Number);
                          const filterYear = dateForFilter.getFullYear();
                          const filterMonth = dateForFilter.getMonth() + 1;
                          if (filterYear !== selectedYear || filterMonth !== selectedMonth) return false;
                        } else {
                          // Year filter
                          const filterYear = dateForFilter.getFullYear();
                          if (filterYear !== parseInt(selectedOrderYear)) return false;
                        }
                        
                        // Fifth filter: Exclude old orders (invoiced/paid and older than 3 months)
                        const threeMonthsAgo = new Date();
                        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                        
                        // If order is invoiced/paid and older than 3 months, exclude it
                        if ((order.status === 'Invoiced' || order.status === 'Paid') && order.dateCreated) {
                          const orderDate = new Date(order.dateCreated);
                          if (orderDate < threeMonthsAgo) {
                            return false;
                          }
                        }
                        
                        return true;
                      })
                      .sort((a, b) => {
                        let aVal, bVal;
                        if (orderSortField === 'startDate') {
                          aVal = new Date(a.startDate || a.start_date || 0);
                          bVal = new Date(b.startDate || b.start_date || 0);
                        } else if (orderSortField === 'endDate') {
                          aVal = new Date(a.endDate || a.end_date || 0);
                          bVal = new Date(b.endDate || b.end_date || 0);
                        } else if (orderSortField === 'value') {
                          aVal = parseFloat(String(a.value || '0').replace(/[^0-9.-]/g, '')) || 0;
                          bVal = parseFloat(String(b.value || '0').replace(/[^0-9.-]/g, '')) || 0;
                        } else if (orderSortField === 'id') {
                          aVal = Number(a.id);
                          bVal = Number(b.id);
                        } else {
                          aVal = (a[orderSortField] || '').toString().toLowerCase();
                          bVal = (b[orderSortField] || '').toString().toLowerCase();
                        }
                        if (aVal < bVal) return orderSortDir === 'asc' ? -1 : 1;
                        if (aVal > bVal) return orderSortDir === 'asc' ? 1 : -1;
                        return 0;
                      })
                      .map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            #{order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.orderType === 'update' ? (order.orderUpdateType || 'Update') : 'Ad Order'}
                            color={order.orderType === 'update' ? 'secondary' : 'primary'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{order.businessName}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={order.status} 
                              color={getStatusColor(order.status)} 
                              size="small" 
                            />
                            {order.updatedAt && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {(() => {
                                  const updated = new Date(order.updatedAt);
                                  const now = new Date();
                                  const diffMs = now - updated;
                                  const diffMins = Math.floor(diffMs / 60000);
                                  const diffHours = Math.floor(diffMs / 3600000);
                                  const diffDays = Math.floor(diffMs / 86400000);
                                  if (diffMins < 60) return `${diffMins}m ago`;
                                  if (diffHours < 24) return `${diffHours}h ago`;
                                  if (diffDays < 7) return `${diffDays}d ago`;
                                  return updated.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                                })()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {order.value}
                          </Typography>
                        </TableCell>
                        <TableCell>{order.salesPerson}</TableCell>
                        <TableCell>{formatDate(order.startDate || order.start_date)}</TableCell>
                        <TableCell>{formatDate(order.endDate || order.end_date)}</TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                              const existingNotes = [];
                              if (order.notes) {
                                existingNotes.push({
                                  id: 'general',
                                  content: order.notes,
                                  createdBy: 'Sales Team',
                                  createdAt: order.dateCreated || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'sales' // Order notes are sales-related
                                });
                              }
                              if (order.designNotes) {
                                existingNotes.push({
                                  id: 'design',
                                  content: order.designNotes,
                                  createdBy: 'Design Team',
                                  createdAt: order.dateCreated || new Date().toISOString(),
                                  isNew: false,
                                  noteType: 'design'
                                });
                              }
                              
                              handleOpenOrderNotesDialog('order', order.id.toString(), order.businessName, existingNotes);
                            }}
                            sx={{ 
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              borderRadius: 1,
                              color: (() => {
                                const totalCount = getTotalNoteCount(order, 'order');
                                return totalCount > 0 ? 'primary.main' : 'text.secondary';
                              })(),
                              '&:hover': {
                                bgcolor: 'action.hover',
                              }
                            }}
                          >
                            Notes
                            {(() => {
                              const totalCount = getTotalNoteCount(order, 'order');
                              return totalCount > 0 ? (
                                <Box
                                  component="span"
                                  sx={{
                                    ml: 0.5,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    px: 0.5,
                                    py: 0.1,
                                    borderRadius: '50%',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    minWidth: 18,
                                    height: 18,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  {totalCount}
                                </Box>
                              ) : null;
                            })()}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {order.status === 'Waiting For Artwork' && (
                              <Button
                                variant="contained"
                                size="small"
                                color="warning"
                                onClick={() => navigate(`/app/attach-artwork/${order.id}`)}
                                sx={{ 
                                  minWidth: 'auto',
                                  px: 2,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  textTransform: 'none'
                                }}
                              >
                                Attach Artwork
                              </Button>
                            )}
                            {order.orderType !== 'update' && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEditOrder(order)}
                                sx={{ 
                                  minWidth: 'auto',
                                  px: 2,
                                  py: 0.5,
                                  fontSize: '0.75rem',
                                  textTransform: 'none'
                                }}
                              >
                                Edit
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Follow Ups Tab */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                  variant={followUpFilter === 'all' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setFollowUpFilter('all')}
                  sx={{ textTransform: 'none' }}
                >
                  All Follow-ups ({followUps.length})
                </Button>
                <Button
                  variant={followUpFilter === 'enquiry' ? 'contained' : 'outlined'}
                  size="small"
                  color="warning"
                  onClick={() => setFollowUpFilter('enquiry')}
                  sx={{ textTransform: 'none' }}
                >
                  Enquiry Follow-ups ({followUps.filter(f => f.source === 'enquiry').length})
                </Button>
                <Button
                  variant={followUpFilter === 'order' ? 'contained' : 'outlined'}
                  size="small"
                  color="success"
                  onClick={() => setFollowUpFilter('order')}
                  sx={{ textTransform: 'none' }}
                >
                  Order Renewals ({followUps.filter(f => f.source === 'order').length})
                </Button>
                <Button
                  variant={followUpFilter === 'outreach' ? 'contained' : 'outlined'}
                  size="small"
                  color="info"
                  onClick={() => setFollowUpFilter('outreach')}
                  sx={{ textTransform: 'none' }}
                >
                  Outreach Follow-ups ({followUps.filter(f => f.source === 'outreach').length})
                </Button>
              </Box>

              {followUps.filter(followUp => {
                if (followUpFilter === 'all') return true;
                return followUp.source === followUpFilter;
              }).length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Business Name</TableCell>
                        <TableCell>Contact Person</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {followUps.filter(followUp => {
                        if (followUpFilter === 'all') return true;
                        return followUp.source === followUpFilter;
                      }).map((followUp) => (
                        <TableRow key={followUp.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {followUp.type === 'Enquiry Follow-Up' ? (
                                <PhoneIcon fontSize="small" color="warning" />
                              ) : followUp.type === 'Order Renewal' ? (
                                <ShoppingCartIcon fontSize="small" color="success" />
                              ) : followUp.type === 'Outreach Follow-Up' ? (
                                <BusinessIcon fontSize="small" color="info" />
                              ) : (
                                <EmailIcon fontSize="small" color="info" />
                              )}
                              <Typography variant="body2">{followUp.type}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {followUp.businessName}
                            </Typography>
                          </TableCell>
                          <TableCell>{followUp.contactPerson}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {formatDate(followUp.dueDate)}
                              </Typography>
                              {new Date(followUp.dueDate) < new Date() && (
                                <WarningIcon fontSize="small" color="error" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={followUp.priority} 
                              color={getPriorityColor(followUp.priority)} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {followUp.notes}
                              </Typography>
                              {followUp.source === 'order' && followUp.orderValue && (
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  Order Value: ${followUp.orderValue}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {followUp.source === 'order' ? (
                                // Order Renewal - Show Reorder and Reoffer buttons
                                <>
                                  <Button
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    onClick={() => handleReorder(followUp)}
                                    sx={{ 
                                      minWidth: 'auto', 
                                      px: 1, 
                                      py: 0.25, 
                                      fontSize: '0.75rem',
                                      textTransform: 'none'
                                    }}
                                  >
                                    Reorder
                                  </Button>
                                  <Button
                                    variant="text"
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleReoffer(followUp)}
                                    sx={{ 
                                      minWidth: 'auto', 
                                      px: 1, 
                                      py: 0.25, 
                                      fontSize: '0.75rem',
                                      textTransform: 'none'
                                    }}
                                  >
                                    Reoffer
                                  </Button>
                                </>
                              ) : followUp.source === 'outreach' ? (
                                // Outreach Follow-Up — navigate to the contact edit page
                                <Button
                                  variant="text"
                                  size="small"
                                  color="info"
                                  onClick={() => navigate(`/app/outreach/edit/${followUp.sourceId}`)}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.25,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                  }}
                                >
                                  View Contact
                                </Button>
                              ) : (
                                // Enquiry Follow-Up - Show notes button
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() => {
                                    const existingNotes = [];
                                    // Collect all types of notes from the original enquiry, just like in the enquiries table
                                    if (followUp.salesNotes) {
                                      existingNotes.push({
                                        id: 'sales',
                                        content: followUp.salesNotes,
                                        createdBy: 'Sales Team',
                                        createdAt: followUp.dueDate || new Date().toISOString(),
                                        isNew: false
                                      });
                                    }
                                    if (followUp.customerNotes) {
                                      existingNotes.push({
                                        id: 'customer',
                                        content: followUp.customerNotes,
                                        createdBy: 'Customer Service',
                                        createdAt: followUp.dueDate || new Date().toISOString(),
                                        isNew: false
                                      });
                                    }
                                    if (followUp.followUpNotes) {
                                      existingNotes.push({
                                        id: 'followup',
                                        content: followUp.followUpNotes,
                                        createdBy: 'Sales Team',
                                        createdAt: followUp.dueDate || new Date().toISOString(),
                                        isNew: false
                                      });
                                    }
                                    // Use the actual enquiry ID from sourceId for the notes dialog
                                    const entityId = followUp.sourceId || followUp.id.toString().replace('enquiry-', '');
                                    handleOpenNotesDialog('enquiry', entityId, followUp.businessName, existingNotes);
                                  }}
                                  sx={{ 
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    color: (() => {
                                      const totalCount = getTotalNoteCount(followUp, 'enquiry');
                                      return totalCount > 0 ? 'warning.main' : 'text.secondary';
                                    })(),
                                    '&:hover': {
                                      bgcolor: 'action.hover',
                                    }
                                  }}
                                >
                                  Notes
                                  {(() => {
                                    const totalCount = getTotalNoteCount(followUp, 'enquiry');
                                    return totalCount > 0 ? (
                                      <Box
                                        component="span"
                                        sx={{
                                          ml: 0.5,
                                          bgcolor: 'warning.main',
                                          color: 'warning.contrastText',
                                          px: 0.5,
                                          py: 0.1,
                                          borderRadius: '50%',
                                          fontSize: '0.65rem',
                                          fontWeight: 600,
                                          minWidth: 18,
                                          height: 18,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        {totalCount}
                                      </Box>
                                    ) : null;
                                  })()}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <ScheduleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                  <Typography variant="h6" color="grey.600" gutterBottom>
                    No Follow-ups Required
                  </Typography>
                  <Typography variant="body2" color="grey.500" sx={{ maxWidth: 400, mx: 'auto' }}>
                    No enquiries with overdue follow-ups and no orders ending in the next month.
                  </Typography>
                </Box>
              )}

              {/* ── Non-Current Follow Ups ───────────────────────────── */}
              <Box sx={{ mt: 5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <ReplayIcon color="warning" />
                  <Typography variant="h6" fontWeight={600}>
                    Non-Current Follow Ups
                  </Typography>
                  {nonCurrentFollowUps.length > 0 && (
                    <Chip label={nonCurrentFollowUps.length} color="warning" size="small" />
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Businesses whose last order ended within the past 2 years but have not renewed.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchNonCurrentFollowUps}
                    disabled={nonCurrentLoading}
                    sx={{ ml: 'auto' }}
                  >
                    Refresh
                  </Button>
                </Box>

                {nonCurrentLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : nonCurrentFollowUps.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <Typography variant="body2">No non-current organisations found.</Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'warning.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Organisation</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Last Order Ended</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Total Orders</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Prev. Consultant</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {nonCurrentFollowUps.map((org) => {
                          const endedDate = org.lastOrderEndDate ? new Date(org.lastOrderEndDate) : null;
                          const monthsAgo = endedDate
                            ? Math.round((Date.now() - endedDate.getTime()) / (1000 * 60 * 60 * 24 * 30))
                            : null;
                          return (
                            <TableRow key={org.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600}>{org.name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {[org.suburb, org.state].filter(Boolean).join(', ') || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {org.primaryPhone && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                    <Typography variant="caption">{org.primaryPhone}</Typography>
                                  </Box>
                                )}
                                {org.primaryEmail && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                    <Typography variant="caption">{org.primaryEmail}</Typography>
                                  </Box>
                                )}
                                {!org.primaryPhone && !org.primaryEmail && '—'}
                              </TableCell>
                              <TableCell>
                                {endedDate ? (
                                  <Box>
                                    <Typography variant="body2" color="warning.main" fontWeight={500}>
                                      {formatDate(org.lastOrderEndDate)}
                                    </Typography>
                                    {monthsAgo !== null && (
                                      <Typography variant="caption" color="text.secondary">
                                        {monthsAgo} month{monthsAgo !== 1 ? 's' : ''} ago
                                      </Typography>
                                    )}
                                  </Box>
                                ) : '—'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{org.totalOrders || '—'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{org.salesConsultantName || '—'}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                  <Tooltip title="Create Offer">
                                    <Button
                                      variant="text"
                                      size="small"
                                      color="secondary"
                                      onClick={() => handleCreateOfferForNonCurrent(org)}
                                      sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}
                                    >
                                      Reoffer
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Add to Business Outreach contact list">
                                    <Button
                                      variant="text"
                                      size="small"
                                      color="warning"
                                      onClick={() => handleAddNonCurrentToOutreach(org)}
                                      sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}
                                    >
                                      Add to Outreach
                                    </Button>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

            </Box>
          </TabPanel>
        </Paper>

        {/* Convert to Business Dialog - Full Organisation Creation */}
        <OrganizationDialog
          open={convertDialogOpen}
          onClose={handleCancelConvert}
          onSubmit={handleCreateOrganization}
          isEdit={false}
          organization={organizationFormData}
          token={token}
          hideSalesConsultant={true}
          defaultSalesConsultantId={user?.id}
        />

        {/* Old Simple Dialog - Keeping for reference but hidden */}
        <Dialog open={false} onClose={handleCancelConvert} maxWidth="md" fullWidth style={{display: 'none'}}>
          <DialogTitle>Convert Enquiry to Business</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Basic Info */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={organizationFormData.name}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business Type"
                  value={organizationFormData.businessType}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, businessType: e.target.value })}
                />
              </Grid>

              {/* Primary Contact */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mt: 1 }}>
                  Primary Contact
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  value={organizationFormData.primaryName}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, primaryName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  value={organizationFormData.primaryEmail}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, primaryEmail: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={organizationFormData.primaryPhone}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, primaryPhone: e.target.value })}
                />
              </Grid>

              {/* Accounts Contact */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mt: 1 }}>
                  Accounts Contact
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  value={organizationFormData.accountsName}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, accountsName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  value={organizationFormData.accountsEmail}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, accountsEmail: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={organizationFormData.accountsPhone}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, accountsPhone: e.target.value })}
                />
              </Grid>

              {/* General Contact */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mt: 1 }}>
                  General Contact
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="General Email"
                  value={organizationFormData.generalEmail}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, generalEmail: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="General Phone"
                  value={organizationFormData.generalPhone}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, generalPhone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Website"
                  value={organizationFormData.website}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, website: e.target.value })}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mt: 1 }}>
                  Address
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={organizationFormData.address}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, address: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Suburb"
                  value={organizationFormData.suburb}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, suburb: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={organizationFormData.state}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, state: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postcode"
                  value={organizationFormData.postcode}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, postcode: e.target.value })}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={organizationFormData.notes}
                  onChange={(e) => setOrganizationFormData({ ...organizationFormData, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelConvert}>Cancel</Button>
            <Button onClick={handleCreateOrganization} variant="contained" color="primary">
              Create Business
            </Button>
          </DialogActions>
        </Dialog>



        {/* Create/Edit Offer Dialog */}
        <Dialog
          open={createOfferDialogOpen}
          onClose={() => { handleCancelCreateOffer(); setShowMapPanel(false); }}
          maxWidth={showMapPanel ? false : 'lg'}
          fullWidth={!showMapPanel}
          PaperProps={showMapPanel ? { sx: { width: '90vw', maxWidth: '90vw', height: 'calc(100vh - 80px)', maxHeight: 'calc(100vh - 80px)' } } : {}}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{isEditingOffer ? 'Edit Offer' : 'Create New Offer'}</span>
            <Tooltip title={showMapPanel ? 'Close map' : 'View organisations on map'}>
              <span>
                <IconButton
                  size="small"
                  color={showMapPanel ? 'primary' : 'default'}
                  onClick={() => setShowMapPanel(v => !v)}
                >
                  <MapIcon />
                </IconButton>
              </span>
            </Tooltip>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', p: 0, overflow: 'hidden', flex: 1 }}>
            {/* Form panel */}
            <Box sx={{ flex: 1, minWidth: 0, p: 3, overflowY: 'auto' }}>
              <OfferForm
                formData={offerFormData}
                setFormData={setOfferFormData}
                organizations={organizations}
                handleAudienceChange={handleOfferAudienceChange}
                addAudience={addOfferAudience}
                removeAudience={removeOfferAudience}
                isEditingOffer={isEditingOffer}
                editingOfferId={editingOfferId}
                onTopSpotConflictsChange={setOfferTopSpotConflicts}
                lockedOrganization={offerFormData.lockedOrganization || null}
              />
            </Box>
            {/* Map panel — fixed width so the form always gets the lion's share */}
            {showMapPanel && (
              <Box sx={{ flex: 1, borderLeft: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <OrganisationsMap
                  organisations={organizations}
                  centerOrgId={offerFormData.organization_id ? String(offerFormData.organization_id) : null}
                  onSelectOrganisation={() => {}}
                  height="100%"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleCancelCreateOffer()} disabled={isSubmittingOffer}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleCreateOffer}
              disabled={
                isSubmittingOffer ||
                !offerFormData.business_name || 
                (offerFormData.businessType === 'existing' && !offerFormData.organization_id) ||
                offerFormData.audiences.some(a => !a.organization_id || !a.channel || !a.adType || !a.startDate || !a.endDate)
                // Note: Removed top spot validation to allow saving offers with conflicts
              }
            >
              {isSubmittingOffer ? 'Saving...' : (isEditingOffer ? 'Update Offer' : 'Save Offer')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reorder Dialog */}
        <Dialog open={reorderDialogOpen} onClose={() => setReorderDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Create Reorder</DialogTitle>
          <DialogContent>
            <AdvertisingOrderForm
              formData={reorderFormData}
              setFormData={setReorderFormData}
              organizations={organizations}
              handleAudienceChange={handleReorderAudienceChange}
              addAudience={addReorderAudience}
              removeAudience={removeReorderAudience}
              lockedOrganization={reorderFormData.lockedOrganization || null}
              onTopSpotConflictsChange={setReorderTopSpotConflicts}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReorderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleReorderSubmit}
              disabled={
                loading ||
                !reorderFormData.organization_id || 
                !reorderFormData.audiences || 
                reorderFormData.audiences.length === 0 ||
                !reorderFormData.audiences[0].organization_id || 
                !reorderFormData.audiences[0].channel || 
                !reorderFormData.audiences[0].startDate || 
                !reorderFormData.audiences[0].endDate || 
                !reorderFormData.audiences[0].price ||
                !checkAddonDurationRequirements(reorderFormData.audiences)
              }
            >
              {loading ? 'Creating...' : 'Create Reorder'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        {/* Order Creation Dialog */}
        <Dialog
          open={createOrderDialogOpen}
          onClose={() => { setCreateOrderDialogOpen(false); setShowMapPanel(false); }}
          maxWidth={showMapPanel ? false : 'lg'}
          fullWidth={!showMapPanel}
          PaperProps={showMapPanel ? { sx: { width: '90vw', maxWidth: '90vw', height: 'calc(100vh - 80px)', maxHeight: 'calc(100vh - 80px)' } } : {}}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Create Order from Offer</span>
            <Tooltip title={showMapPanel ? 'Close map' : 'View organisations on map'}>
              <span>
                <IconButton
                  size="small"
                  color={showMapPanel ? 'primary' : 'default'}
                  onClick={() => setShowMapPanel(v => !v)}
                >
                  <MapIcon />
                </IconButton>
              </span>
            </Tooltip>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', p: 0, overflow: 'hidden', flex: 1 }}>
            {/* Form panel */}
            <Box sx={{ flex: 1, minWidth: 0, p: 3, overflowY: 'auto' }}>
            {/* Warn if any audience dates from the offer are in the past */}
            {orderFormData.audiences?.some(a => a.startDate && new Date(a.startDate) < new Date(getCurrentDateForInput())) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Date Warning:</strong> One or more audience start dates from this offer are in the past. Please update the dates before creating the order.
              </Alert>
            )}
            {orderFormData.audiences?.some(a => a.endDate && new Date(a.endDate) < new Date(getCurrentDateForInput())) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Date Warning:</strong> One or more audience end dates from this offer are in the past. Please update the dates before creating the order.
              </Alert>
            )}
            <AdvertisingOrderForm
              formData={orderFormData}
              setFormData={setOrderFormData}
              organizations={organizations}
              handleAudienceChange={handleAudienceChange}
              addAudience={addAudience}
              removeAudience={removeAudience}
              lockedOrganization={selectedOfferForOrder ? {
                id: selectedOfferForOrder.organizationId,
                name: selectedOfferForOrder.businessName
              } : null}
              onTopSpotConflictsChange={setOrderTopSpotConflicts}
            />
            </Box>
            {/* Map panel — fixed width so the form always gets the lion's share */}
            {showMapPanel && (
              <Box sx={{ flex: 1, borderLeft: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <OrganisationsMap
                  organisations={organizations}
                  centerOrgId={orderFormData.organization_id ? String(orderFormData.organization_id) : (selectedOfferForOrder?.organizationId ? String(selectedOfferForOrder.organizationId) : null)}
                  onSelectOrganisation={() => {}}
                  height="100%"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              disabled={
                !orderFormData.organization_id || 
                !orderFormData.audiences || 
                orderFormData.audiences.length === 0 ||
                !orderFormData.audiences[0].organization_id || 
                !orderFormData.audiences[0].channel || 
                !orderFormData.audiences[0].startDate || 
                !orderFormData.audiences[0].endDate || 
                !orderFormData.audiences[0].price ||
                !checkAddonDurationRequirements(orderFormData.audiences) ||
                !checkOrderTopSpotRequirements(orderFormData.audiences, orderTopSpotConflicts)
              }
              onClick={async () => {
                try {
                  setSnackbar({ 
                    open: true, 
                    message: 'Creating order...', 
                    severity: 'info' 
                  });

                  const token = await getToken();
                  
                  // Extract temp attachments before sending order data
                  const { tempAttachments, ...orderDataWithoutAttachments } = orderFormData;
                  
                  // Combine notes properly for backend
                  // Backend expects 'notes' (sales notes) and 'designNotes' fields.
                  // No "Order created from offer #X" marker is added here - the original
                  // offer note (already labelled "(Offer Creation)") is what should carry
                  // over, and the offer_id field already tracks the offer -> order link.
                  const combinedSalesNotes = [
                    orderFormData.salesNotesFromOffer, // Original offer sales notes
                    orderFormData.salesNotes // Additional sales notes for order
                  ].filter(Boolean).join('\n\n');
                  
                  const combinedDesignNotes = [
                    orderFormData.designNotesFromOffer, // Original offer design notes
                    orderFormData.designNotes // Additional design notes for order
                  ].filter(Boolean).join('\n\n');

                  // Update the order data with properly formatted notes
                  orderDataWithoutAttachments.notes = combinedSalesNotes || null;
                  orderDataWithoutAttachments.designNotes = combinedDesignNotes || null;
                  
                  // Remove the individual notes fields that aren't expected by backend
                  delete orderDataWithoutAttachments.salesNotes;
                  delete orderDataWithoutAttachments.salesNotesFromOffer;
                  delete orderDataWithoutAttachments.designNotesFromOffer;
                  delete orderDataWithoutAttachments.customerNotes;
                  delete orderDataWithoutAttachments.customerNotesFromOffer;
                  
                  // Add offer_id if this order is being created from an offer
                  if (selectedOfferForOrder) {
                    orderDataWithoutAttachments.offer_id = selectedOfferForOrder.id;
                  }
                  
                  const response = await fetch(`${API_URL}/ad-orders`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(orderDataWithoutAttachments)
                  });

                  if (response.ok) {
                    const result = await response.json();
                    const orderId = result.data?.id;

                    // Copy attachments from offer to order if this was created from an offer
                    let offerAttachmentCopyErrors = [];
                    if (selectedOfferForOrder && orderId) {
                      try {
                        // Fetch offer attachments
                        const offerAttachmentsResponse = await fetch(
                          `${API_URL}/offer-attachments/${selectedOfferForOrder.id}`,
                          {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          }
                        );

                        if (offerAttachmentsResponse.ok) {
                          const offerAttachmentsData = await offerAttachmentsResponse.json();
                          const offerAttachments = offerAttachmentsData.data || [];

                          if (offerAttachments.length > 0) {
                            setSnackbar({ 
                              open: true, 
                              message: `Order created! Copying ${offerAttachments.length} attachments from offer...`, 
                              severity: 'info' 
                            });

                            // Download each offer attachment and re-upload to the order
                            for (const attachment of offerAttachments) {
                              try {
                                // Download the file from the offer
                                let fileResponse = await fetch(
                                  `${API_URL}/offer-attachments/download/${attachment.id}`,
                                  {
                                    headers: {
                                      'Authorization': `Bearer ${token}`
                                    }
                                  }
                                );

                                // Fall back to the static /uploads URL (same one used for
                                // the preview thumbnail) if the authenticated download
                                // endpoint isn't available/deployed yet or 404s.
                                if (!fileResponse.ok && attachment.filePath) {
                                  fileResponse = await fetch(
                                    `${API_URL.replace('/api', '')}/uploads/${attachment.filePath}`
                                  );
                                }

                                if (!fileResponse.ok) {
                                  throw new Error(`Failed to download "${attachment.originalName}" from offer (status ${fileResponse.status})`);
                                }

                                const blob = await fileResponse.blob();
                                const file = new File([blob], attachment.originalName, { type: attachment.mimeType });

                                // Upload to the order
                                const formData = new FormData();
                                formData.append('attachments', file);

                                const uploadResponse = await fetch(`${API_URL}/order-attachments/${orderId}`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: formData
                                });

                                // fetch() only rejects on network failure, never on HTTP error
                                // status - without checking .ok here, a failed copy (e.g. 400/401/500)
                                // would silently succeed from the UI's perspective, leaving the order
                                // with no attachments and no indication anything went wrong.
                                if (!uploadResponse.ok) {
                                  const errorBody = await uploadResponse.json().catch(() => ({}));
                                  throw new Error(errorBody.message || `Failed to attach "${attachment.originalName}" to the order (status ${uploadResponse.status})`);
                                }
                              } catch (fileError) {
                                console.error(`Error copying attachment ${attachment.originalName}:`, fileError);
                                offerAttachmentCopyErrors.push(`${attachment.originalName} (${fileError.message})`);
                              }
                            }
                          }
                        } else {
                          offerAttachmentCopyErrors.push('(could not read offer attachments)');
                        }
                      } catch (offerAttachmentError) {
                        console.error('Error fetching/copying offer attachments:', offerAttachmentError);
                        offerAttachmentCopyErrors.push('(could not read offer attachments)');
                      }

                      // Also copy the offer's single embedded "Offer Image" (offer.imageData),
                      // which is a separate feature from offer_attachments and was previously
                      // silently lost when converting an offer to an order. Only copy it if the
                      // user hasn't removed it from the attachments list in the form.
                      const offerImageStillIncluded = tempAttachments &&
                        tempAttachments.some(att => att.fromOfferImage);
                      if (offerImageStillIncluded && selectedOfferForOrder.imageData) {
                        try {
                          const parsedImage = parseImageDataUrl(selectedOfferForOrder.imageData);
                          if (!parsedImage) {
                            throw new Error('Offer image is not in the expected format');
                          }
                          const { mimeType, base64Data } = parsedImage;
                          const byteChars = atob(base64Data);
                          const byteNumbers = new Array(byteChars.length);
                          for (let i = 0; i < byteChars.length; i++) {
                            byteNumbers[i] = byteChars.charCodeAt(i);
                          }
                          const extension = (mimeType.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
                          const imageFile = new File([new Uint8Array(byteNumbers)], `offer-image.${extension}`, { type: mimeType });

                          const imageFormData = new FormData();
                          imageFormData.append('attachments', imageFile);

                          const imageUploadResponse = await fetch(`${API_URL}/order-attachments/${orderId}`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`
                            },
                            body: imageFormData
                          });

                          if (!imageUploadResponse.ok) {
                            const errorBody = await imageUploadResponse.json().catch(() => ({}));
                            throw new Error(errorBody.message || `Failed to attach offer image to the order (status ${imageUploadResponse.status})`);
                          }
                        } catch (imageError) {
                          console.error('Error copying offer image to order:', imageError);
                          offerAttachmentCopyErrors.push(`offer image (${imageError.message})`);
                        }
                      }

                      if (offerAttachmentCopyErrors.length > 0) {
                        setSnackbar({
                          open: true,
                          message: `Order created, but failed to copy ${offerAttachmentCopyErrors.length} attachment(s) from the offer: ${offerAttachmentCopyErrors.join(', ')}`,
                          severity: 'warning'
                        });
                      }
                    }

                    // Upload NEW attachments (exclude attachments from offer as they're already copied)
                    const newAttachments = tempAttachments ? tempAttachments.filter(att => !att.fromOffer) : [];
                    const offerCopySuffix = offerAttachmentCopyErrors.length > 0
                      ? ` (Warning: failed to copy ${offerAttachmentCopyErrors.length} attachment(s) from the offer: ${offerAttachmentCopyErrors.join(', ')})`
                      : '';

                    if (newAttachments.length > 0 && orderId) {
                      try {
                        setSnackbar({ 
                          open: true, 
                          message: `Order created! Uploading ${newAttachments.length} new attachment(s)...`, 
                          severity: 'info' 
                        });

                        await uploadTempAttachments(orderId, newAttachments, getToken);
                        
                        const totalAttachments = tempAttachments.length;
                        setSnackbar({ 
                          open: true, 
                          message: `Order created successfully with ${totalAttachments} attachment(s)!${offerCopySuffix}`, 
                          severity: offerCopySuffix ? 'warning' : 'success' 
                        });
                      } catch (attachmentError) {
                        console.error('Error uploading attachments:', attachmentError);
                        setSnackbar({ 
                          open: true, 
                          message: `Order created but failed to upload new attachments: ${attachmentError.message}${offerCopySuffix}`, 
                          severity: 'warning' 
                        });
                      }
                    } else {
                      const totalAttachments = tempAttachments ? tempAttachments.length : 0;
                      setSnackbar({ 
                        open: true, 
                        message: (totalAttachments > 0 
                          ? `Order created successfully with ${totalAttachments} attachment(s)!`
                          : 'Order created successfully!') + offerCopySuffix, 
                        severity: offerCopySuffix ? 'warning' : 'success'
                      });
                    }

                    // Archive the offer if this order was created from an offer
                    if (selectedOfferForOrder) {
                      try {
                        await fetch(`${API_URL}/offers/${selectedOfferForOrder.id}/archive`, {
                          method: 'PUT',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          }
                        });
                      } catch (archiveError) {
                        console.error('Error archiving offer:', archiveError);
                        // Don't show error to user - archiving is a background operation
                      }
                    }

                    setCreateOrderDialogOpen(false);
                    loadSalesData(); // Refresh data
                  } else {
                    throw new Error('Failed to create order');
                  }
                } catch (error) {
                  console.error('Error creating order:', error);
                  setSnackbar({ 
                    open: true, 
                    message: `Error creating order: ${error.message}`, 
                    severity: 'error' 
                  });
                }
              }}
              color="primary"
            >
              Create Ad Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Requests Dialog */}
        <Dialog 
          open={changeRequestsDialog.open} 
          onClose={() => setChangeRequestsDialog({ open: false, offerId: null, requests: [] })}
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            Client Change Requests - Offer #{changeRequestsDialog.offerId}
          </DialogTitle>
          <DialogContent>
            {changeRequestsDialog.requests.length === 0 ? (
              <Typography>No change requests found.</Typography>
            ) : (
              <Stack spacing={2}>
                {changeRequestsDialog.requests.map((request, index) => (
                  <Paper key={request.id} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Request #{index + 1} - {formatDate(request.requestedAt)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                      {request.changeNotes}
                    </Typography>
                    {request.attachmentOriginalName && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Attachment:
                        </Typography>
                        {isImageFile(request.attachmentOriginalName) ? (
                          // Image attachment with preview
                          <Card sx={{ maxWidth: 300, mb: 1 }}>
                            <CardMedia
                              component="img"
                              height="120"
                              image={getAttachmentUrl(request.attachmentPath)}
                              alt={request.attachmentOriginalName}
                              sx={{ 
                                cursor: 'pointer',
                                objectFit: 'cover'
                              }}
                              onClick={() => handleViewImage(request.attachmentPath, request.attachmentOriginalName)}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <Box 
                              sx={{ 
                                display: 'none',
                                height: 120,
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.100',
                                color: 'text.secondary'
                              }}
                            >
                              <ImageIcon />
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                Image not available
                              </Typography>
                            </Box>
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="caption" sx={{ flex: 1, mr: 1 }}>
                                  {request.attachmentOriginalName}
                                </Typography>
                                <Box>
                                  <Tooltip title="View full size">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleViewImage(request.attachmentPath, request.attachmentOriginalName)}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleDownloadAttachment(request.attachmentPath, request.attachmentOriginalName)}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        ) : (
                          // Non-image attachment
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={`📎 ${request.attachmentOriginalName}`}
                              size="small"
                              variant="outlined"
                            />
                            <Tooltip title="Download">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDownloadAttachment(request.attachmentPath, request.attachmentOriginalName)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    )}
                    {request.processed === true && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="caption" color="success.dark">
                          ? Processed by {request.processedByName} on {formatDate(request.processedAt)}
                        </Typography>
                        {request.responseNotes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Response: {request.responseNotes}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangeRequestsDialog({ open: false, offerId: null, requests: [] })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Viewer Dialog */}
        <Dialog
          open={imageViewerDialog.open}
          onClose={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {imageViewerDialog.imageName}
            <IconButton
              onClick={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: 300,
              bgcolor: 'grey.50'
            }}>
              {imageViewerDialog.imageUrl ? (
                <img
                  src={imageViewerDialog.imageUrl}
                  alt={imageViewerDialog.imageName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <Skeleton variant="rectangular" width="100%" height={300} />
              )}
              <Box sx={{ 
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300,
                color: 'text.secondary'
              }}>
                <ImageIcon sx={{ fontSize: 64, mb: 2 }} />
                <Typography variant="body1">
                  Unable to load image
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => {
                // Extract attachment path from image URL
                const urlParts = imageViewerDialog.imageUrl.split('/uploads/');
                if (urlParts.length > 1) {
                  handleDownloadAttachment(urlParts[1], imageViewerDialog.imageName);
                }
              }}
            >
              Download
            </Button>
            <Button onClick={() => setImageViewerDialog({ open: false, imageUrl: '', imageName: '' })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notes Dialog */}
        <NotesDialog
          open={notesDialog.open}
          onClose={handleCloseNotesDialog}
          title={notesDialog.title}
          entityType={notesDialog.entityType}
          entityId={notesDialog.entityId}
          initialNotes={notesDialog.notes}
          defaultNoteType={notesDialog.defaultNoteType}
          onNotesUpdate={handleNotesUpdate}
        />

        {/* Order Notes Dialog - Order Workflow Version for Order Tracking */}
        <OrderWorkflowNotesDialog
          open={orderNotesDialog.open}
          onClose={handleCloseOrderNotesDialog}
          title={orderNotesDialog.title}
          entityType={orderNotesDialog.entityType}
          entityId={orderNotesDialog.entityId}
          initialNotes={orderNotesDialog.notes}
          onNotesUpdate={handleOrderNotesUpdate}
        />

        {/* Edit Order Dialog */}
        <Dialog open={editOrderDialogOpen} onClose={() => setEditOrderDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Edit Order #{editingOrder?.id}</DialogTitle>
          <DialogContent>
            <AdvertisingOrderForm
              formData={editOrderFormData}
              setFormData={setEditOrderFormData}
              organizations={organizations}
              handleAudienceChange={handleEditOrderAudienceChange}
              addAudience={(newAudience) => {
                setEditOrderFormData(prev => ({
                  ...prev,
                  audiences: [...(prev.audiences || []), newAudience]
                }));
              }}
              removeAudience={(index) => {
                setEditOrderFormData(prev => ({
                  ...prev,
                  audiences: prev.audiences.filter((_, i) => i !== index)
                }));
              }}
              lockedOrganization={editOrderFormData.organization_id ? {
                id: editOrderFormData.organization_id,
                name: editOrderFormData.businessName
              } : null}
              onTopSpotConflictsChange={setEditOrderTopSpotConflicts}
              isEditingOrder={true}
              editingOrderId={editingOrder?.id}
            />
            {editingOrder?.id && (
              <WorkflowHistory orderId={editingOrder.id} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOrderDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveEditedOrder}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Comprehensive Update Dialog */}
        <ComprehensiveUpdateDialog
          open={createUpdateDialogOpen}
          onClose={handleCloseUpdateDialog}
          order={selectedOrderForUpdate}
          onSubmit={handleComprehensiveUpdateSubmit}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          message={snackbar.message}
          ContentProps={{
            sx: {
              bgcolor:
                snackbar.severity === 'error' ? 'error.main'
                : snackbar.severity === 'success' ? 'success.main'
                : snackbar.severity === 'warning' ? 'warning.main'
                : 'info.main',
              color: 'white',
              fontWeight: 500
            }
          }}
        />

      </Container>
  );
};

export default SalesWorkflow; 